import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  BarChart3,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Download,
  Eye,
  Filter,
  GripVertical,
  Layers,
  LineChart,
  Lock,
  LogOut,
  Maximize2,
  Pencil,
  ScatterChart,
  Search,
  Send,
  Star,
  Table2,
  ShieldCheck,
  Trash2,
  UploadCloud,
  X
} from "lucide-react";
import {
  ApiClient,
  type Api,
  type ClusterItem,
  type ClusterSummary,
  type IndustrySummary,
  type ResolvedCluster,
  type ResolvedClusterChart
} from "./api";
import {
  columnValues,
  defaultScatterAxes,
  latestPercentile,
  parseScatterRows,
  readWorkbookTable,
  type WorkbookTable
} from "./chartParser";
import { parseIndustryWorkbook } from "./industryParser";
import {
  buildComparisonCharts,
  buildDualAxisChart,
  buildScatterChart,
  buildQuantileForecastChart,
  LocalApiClient,
  type ComparisonOptions,
  type DualAxisOptions,
  type ScatterOptions,
  type QuantileForecastOptions
} from "./localApi";
import {
  confirmPasswordReset,
  getCurrentUser,
  getIdToken,
  requestPasswordReset,
  signIn,
  signOut,
  type AuthUser,
  type NewPasswordChallenge
} from "./auth";
import { config } from "./config";
import type { ChartSpec, ReportDetail, ReportSummary } from "./types";

function hasRole(user: AuthUser | null, role: "Admin" | "Member"): boolean {
  return Boolean(user?.roles.includes(role));
}

export function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [view, setView] = useState<"reports" | "dashboards" | "clusters" | "admin" | "industries">("reports");
  const api = useMemo<Api | null>(() => {
    if (config.localMock) return new LocalApiClient();
    if (!user) return null;
    return new ApiClient(async () => {
      const token = await getIdToken();
      if (!token) {
        signOut();
        setUser(null);
        throw new Error("세션이 만료되어 로그아웃되었습니다. 다시 로그인해주세요.");
      }
      return token;
    });
  }, [user]);

  useEffect(() => {
    // Local offline test mode: skip Cognito and act as a mock Admin so the
    // upload -> chart flow can be exercised without any backend.
    if (config.localMock) {
      setUser({ email: "admin@local.test", sub: "local-admin", roles: ["Admin"], idToken: "local-mock" });
      setView("admin");
      setAuthLoading(false);
      return;
    }
    getCurrentUser()
      .then((current) => {
        setUser(current);
        setView(hasRole(current, "Admin") ? "admin" : "reports");
      })
      .finally(() => setAuthLoading(false));
  }, []);

  if (authLoading) {
    return <Shell title="Yangtze Capitals" subtitle="Loading secure workspace..." />;
  }

  if (!user) {
    return <LoginScreen onSignedIn={setUser} />;
  }

  const isAdmin = hasRole(user, "Admin");

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">YC</div>
          <div>
            <strong>Yangtze Capitals</strong>
            <span>Market intelligence</span>
          </div>
        </div>
        <nav className="nav-list">
          <span className="nav-group">대시보드</span>
          <button className={view === "reports" ? "active" : ""} onClick={() => setView("reports")}>
            <BarChart3 size={18} />
            지표 대시보드
          </button>
          <button className={view === "dashboards" ? "active" : ""} onClick={() => setView("dashboards")}>
            <Layers size={18} />
            산업 대시보드
          </button>
          <button className={view === "clusters" ? "active" : ""} onClick={() => setView("clusters")}>
            <Star size={18} />
            지표 클러스터
          </button>
          {isAdmin && (
            <>
              <span className="nav-group admin">
                <ShieldCheck size={12} /> 분석 (관리자)
              </span>
              <button
                className={`nav-admin ${view === "admin" ? "active" : ""}`}
                onClick={() => setView("admin")}
              >
                <BarChart3 size={18} />
                지표 분석
              </button>
              <button
                className={`nav-admin ${view === "industries" ? "active" : ""}`}
                onClick={() => setView("industries")}
              >
                <Layers size={18} />
                산업 분석
              </button>
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <div>
            <strong>{user.email}</strong>
            <span>{user.roles.join(", ") || "Member"}</span>
          </div>
          {!config.localMock && (
            <button
              className="icon-button"
              title="Sign out"
              onClick={() => {
                signOut();
                setUser(null);
              }}
            >
              <LogOut size={18} />
            </button>
          )}
        </div>
      </aside>
      <main className="workspace">
        {view === "admin" && isAdmin ? (
          <AdminDashboard api={api} onShowReports={() => setView("reports")} />
        ) : view === "industries" && isAdmin ? (
          <IndustryManager api={api} onShowDashboards={() => setView("dashboards")} />
        ) : view === "dashboards" ? (
          <DashboardsPage api={api} user={user} />
        ) : view === "clusters" ? (
          <ClustersPage api={api} user={user} />
        ) : (
          <ReportsPage api={api} user={user} />
        )}
      </main>
    </div>
  );
}

function Shell({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="center-screen">
      <div className="loader-panel">
        <div className="brand-mark">YC</div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
    </div>
  );
}

// "signin" is the normal flow; "reset-request" asks Cognito to email a code;
// "reset-confirm" takes that code + a new password. The newPasswordRequired
// challenge (first-login forced reset) is tracked separately via `challenge`.
type LoginMode = "signin" | "reset-request" | "reset-confirm";

function LoginScreen({ onSignedIn }: { onSignedIn: (user: AuthUser) => void }) {
  const [mode, setMode] = useState<LoginMode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [challenge, setChallenge] = useState<NewPasswordChallenge | null>(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  function goToMode(next: LoginMode) {
    setMode(next);
    setError("");
    setInfo("");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      if (challenge) {
        onSignedIn(await challenge.complete(newPassword));
        return;
      }

      if (mode === "reset-request") {
        await requestPasswordReset(email);
        setMode("reset-confirm");
        setInfo("We emailed you a verification code. Enter it with your new password.");
        return;
      }

      if (mode === "reset-confirm") {
        await confirmPasswordReset(email, resetCode.trim(), newPassword);
        setMode("signin");
        setPassword("");
        setNewPassword("");
        setResetCode("");
        setInfo("Password updated. Sign in with your new password.");
        return;
      }

      const result = await signIn(email, password);
      if ("type" in result && result.type === "newPasswordRequired") {
        setChallenge(result);
        return;
      }
      if ("idToken" in result) {
        onSignedIn(result);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to complete the request.");
    } finally {
      setLoading(false);
    }
  }

  const heading = challenge
    ? "Set new password"
    : mode === "reset-request"
      ? "Reset password"
      : mode === "reset-confirm"
        ? "Set a new password"
        : "Sign in";
  const submitLabel = loading
    ? "Working..."
    : challenge
      ? "Set password"
      : mode === "reset-request"
        ? "Send reset code"
        : mode === "reset-confirm"
          ? "Update password"
          : "Sign in";

  return (
    <div className="login-layout">
      <section className="login-hero">
        <div className="brand">
          <div className="brand-mark">YC</div>
          <div>
            <strong>Yangtze Capitals</strong>
            <span>Market intelligence</span>
          </div>
        </div>
        <h1>Global secondary market data, structured into decision-ready intelligence.</h1>
        <p>
          We transform complex market data into clear, curated charts and reports — built for analysts and
          delivered to your members.
        </p>
      </section>
      <form className="login-panel" onSubmit={submit}>
        <Lock size={22} />
        <h2>{heading}</h2>

        {challenge ? (
          <label>
            New password
            <input
              type="password"
              value={newPassword}
              minLength={8}
              onChange={(event) => setNewPassword(event.target.value)}
              required
            />
          </label>
        ) : mode === "signin" ? (
          <>
            <label>
              Email
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
          </>
        ) : mode === "reset-request" ? (
          <label>
            Email
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
        ) : (
          <>
            <label>
              Email
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </label>
            <label>
              Verification code
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={resetCode}
                onChange={(event) => setResetCode(event.target.value)}
                required
              />
            </label>
            <label>
              New password
              <input
                type="password"
                value={newPassword}
                minLength={8}
                onChange={(event) => setNewPassword(event.target.value)}
                required
              />
            </label>
          </>
        )}

        {info && <div className="notice">{info}</div>}
        {error && <div className="error">{error}</div>}

        <div className="login-actions">
          <button className="primary-button" type="submit" disabled={loading}>
            <Send size={18} />
            {submitLabel}
          </button>
          {!challenge && mode === "signin" && (
            <button type="button" className="secondary-button" onClick={() => goToMode("reset-request")}>
              Forgot password
            </button>
          )}
        </div>

        {!challenge && mode !== "signin" && (
          <button type="button" className="link-button" onClick={() => goToMode("signin")}>
            ← Back to sign in
          </button>
        )}
      </form>
    </div>
  );
}

function AdminDashboard({ api, onShowReports }: { api: Api | null; onShowReports?: () => void }) {
  const [message, setMessage] = useState("");
  const [activeTool, setActiveTool] = useState<"compare" | "dual" | "quantile" | "scatter">("compare");
  const [files, setFiles] = useState<File[]>([]);
  const [previewCharts, setPreviewCharts] = useState<ChartSpec[] | null>(null);
  const [fileLabels, setFileLabels] = useState<string[]>([]);
  const [cmpTitle, setCmpTitle] = useState("");
  const [cmpUnit, setCmpUnit] = useState("");
  const [cmpCategory, setCmpCategory] = useState("");
  const [cmpBusy, setCmpBusy] = useState(false);
  const [columnOptions, setColumnOptions] = useState<string[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [columnTitles, setColumnTitles] = useState<Record<string, string>>({});
  const [rangeOptions, setRangeOptions] = useState<string[]>([]);
  const [fromLabel, setFromLabel] = useState("");
  const [toLabel, setToLabel] = useState("");
  const [yMinInput, setYMinInput] = useState("");
  const [yMaxInput, setYMaxInput] = useState("");

  // When files are chosen, read them once: offer the data columns they share
  // (Y-axis metrics, multi-select) and the union of first-column labels
  // (X-axis time range).
  useEffect(() => {
    let cancelled = false;
    if (files.length < 1) {
      setColumnOptions([]);
      setRangeOptions([]);
      return;
    }
    void (async () => {
      try {
        const tables = await Promise.all(files.map(async (f) => await readWorkbookTable(await f.arrayBuffer())));
        // Only columns whose header name is identical across ALL uploaded files.
        const columns = (tables[0]?.columns ?? []).filter((name) => tables.every((t) => t.columns.includes(name)));
        const labels: string[] = [];
        for (const table of tables) {
          for (const label of table.labels) {
            if (!labels.includes(label)) labels.push(label);
          }
        }
        if (cancelled) return;
        setColumnOptions(columns);
        setSelectedColumns((prev) => {
          const valid = prev.filter((name) => columns.includes(name));
          return valid.length ? valid : columns[0] ? [columns[0]] : [];
        });
        setRangeOptions(labels);
        setFromLabel((prev) => (labels.includes(prev) ? prev : labels[0] ?? ""));
        setToLabel((prev) => (labels.includes(prev) ? prev : labels[labels.length - 1] ?? ""));
      } catch {
        if (!cancelled) {
          setColumnOptions([]);
          setRangeOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [files]);

  function toggleColumn(name: string) {
    setSelectedColumns((prev) => (prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]));
    setPreviewCharts(null); // selection changed -> stale preview
  }

  function comparisonOptions(): ComparisonOptions {
    const yMin = yMinInput.trim() === "" ? undefined : Number(yMinInput);
    const yMax = yMaxInput.trim() === "" ? undefined : Number(yMaxInput);
    return {
      columns: selectedColumns,
      columnTitles,
      names: fileLabels,
      fromLabel: fromLabel || undefined,
      toLabel: toLabel || undefined,
      yMin: Number.isFinite(yMin) ? yMin : undefined,
      yMax: Number.isFinite(yMax) ? yMax : undefined,
      title: cmpTitle,
      unit: cmpUnit,
      category: cmpCategory
    };
  }

  async function preview() {
    if (!files.length || !selectedColumns.length) return;
    setMessage("");
    try {
      const { charts } = await buildComparisonCharts(files, comparisonOptions());
      setPreviewCharts(charts);
    } catch (caught) {
      setPreviewCharts(null);
      setMessage(caught instanceof Error ? caught.message : "미리보기 생성에 실패했습니다.");
    }
  }

  async function compareCompanies() {
    if (!api || !files.length || !selectedColumns.length) return;
    setCmpBusy(true);
    setMessage("");
    try {
      await api.createComparison(files, comparisonOptions());
      setFiles([]);
      setFileLabels([]);
      setSelectedColumns([]);
      setColumnTitles({});
      setPreviewCharts(null);
      setMessage("비교 리포트를 생성했습니다. 지표 대시보드에서 확인하세요.");
      onShowReports?.();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "비교 생성에 실패했습니다.");
    } finally {
      setCmpBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <h1>지표 분석</h1>
          <p>엑셀을 올려 즉석에서 비교·분석 차트를 만들어 게시합니다 (관리자 전용).</p>
        </div>
      </header>

      <section className="panel">
        <h2>분석 도구</h2>
        <div className="toolbox">
          <button
            type="button"
            className={activeTool === "compare" ? "tool-button active" : "tool-button"}
            onClick={() => setActiveTool("compare")}
          >
            <span className="tool-icon">
              <LineChart size={20} />
            </span>
            <span>
              <strong>시계열 종목비교</strong>
              <span>여러 회사·지표를 기간별 라인 차트로 비교</span>
            </span>
          </button>
          <button
            type="button"
            className={activeTool === "dual" ? "tool-button active" : "tool-button"}
            onClick={() => setActiveTool("dual")}
          >
            <span className="tool-icon">
              <LineChart size={20} />
            </span>
            <span>
              <strong>이중축 시계열 비교</strong>
              <span>좌/우 2개 축으로 단위 다른 지표 동시 비교</span>
            </span>
          </button>
          <button
            type="button"
            className={activeTool === "quantile" ? "tool-button active" : "tool-button"}
            onClick={() => setActiveTool("quantile")}
          >
            <span className="tool-icon">
              <LineChart size={20} />
            </span>
            <span>
              <strong>과거 분위 미래 예측</strong>
              <span>분위(과거 분포 내 위치) vs 예측값 산점도</span>
            </span>
          </button>
          <button
            type="button"
            className={activeTool === "scatter" ? "tool-button active" : "tool-button"}
            onClick={() => setActiveTool("scatter")}
          >
            <span className="tool-icon">
              <ScatterChart size={20} />
            </span>
            <span>
              <strong>산업 산점도</strong>
              <span>분류·산업별 두 지표(X·Y) 산점도 — 모멘텀(20일·5일), 拥挤度 분포 등</span>
            </span>
          </button>
          {/* 향후 도구 버튼이 이 영역에 계속 추가됩니다 */}
        </div>
      </section>

      {activeTool === "compare" && (
        <section className="panel">
          <h2>시계열 종목비교</h2>
          <p style={{ color: "#607089", marginTop: 6, fontSize: 13 }}>
            엑셀을 올리고 비교할 지표·기간을 고르면 차트를 만듭니다 — 파일 1개면 그 종목의 지표 추이,
            여러 개면 종목 간 비교가 됩니다. 엑셀의 <strong>1열은 시계열(날짜/기간) 축</strong>입니다.
          </p>

          <div className="form-grid">
            <div className="form-section">
              <div className="section-head">
                <span className="step">1</span> 회사 파일 · 비교 지표
                <span className="section-hint">엑셀 선택 → 범례 이름 → 지표 체크</span>
              </div>

              <div className="fields-row">
                <div className="field file-field">
                  <label>회사 엑셀 파일</label>
                  <label className={`file-input file-dropzone${files.length ? " has-files" : ""}`}>
                    <span className="dropzone-top">
                      <UploadCloud size={20} />
                      {files.length ? `${files.length}개 파일 선택됨 — 다시 클릭해 변경` : "클릭하여 엑셀 파일 업로드"}
                    </span>
                    <span className="dropzone-hint">.xlsx · .xls · .csv (1개 이상 · 여러 개면 종목 비교)</span>
                    <input
                      type="file"
                      multiple
                      accept=".xlsx,.xls,.csv"
                      onChange={(event) => {
                        const picked = Array.from(event.target.files ?? []);
                        setFiles(picked);
                        setFileLabels(picked.map((f) => f.name.replace(/\.[^.]+$/, "")));
                        setPreviewCharts(null);
                      }}
                    />
                  </label>
                </div>
                <div className="field key" style={{ minWidth: 200 }}>
                  <label>리포트 제목</label>
                  <input placeholder="예: 홍콩 3부작" value={cmpTitle} onChange={(event) => setCmpTitle(event.target.value)} />
                  <span className="field-note">차트 제목으로 사용 · 비우면 자동 생성</span>
                </div>
                <div className="field" style={{ minWidth: 120 }}>
                  <label>단위</label>
                  <input placeholder="예: 元" value={cmpUnit} onChange={(event) => setCmpUnit(event.target.value)} />
                </div>
                <div className="field" style={{ minWidth: 150 }}>
                  <label>카테고리</label>
                  <input
                    placeholder="Report list 묶음명"
                    value={cmpCategory}
                    onChange={(event) => setCmpCategory(event.target.value)}
                  />
                </div>
              </div>

              {files.length > 0 && (
                <div className="file-cards">
                  {files.map((f, index) => (
                    <div className="file-card" key={`${f.name}-${index}`}>
                      <div className="card-row">
                        <span className="card-key">파일명</span>
                        <span className="file-orig" title={f.name}>{f.name}</span>
                      </div>
                      <div className="card-row edit">
                        <span className="card-key">표시명</span>
                        <input
                          className="file-name-input"
                          value={fileLabels[index] ?? ""}
                          placeholder="범례에 표시할 이름"
                          onChange={(event) =>
                            setFileLabels((prev) => prev.map((value, i) => (i === index ? event.target.value : value)))
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="sub-head">
                비교할 지표 (Y축)
                <span className="section-hint">카드를 클릭해 선택(여러 개) · 표시값은 차트 제목</span>
              </div>
              {columnOptions.length ? (
                <div className="metric-grid">
                  {columnOptions.map((name) => {
                    const on = selectedColumns.includes(name);
                    return (
                      <div
                        key={name}
                        className={on ? "metric-chip checked" : "metric-chip"}
                        role="button"
                        aria-pressed={on}
                        title="클릭하여 선택/해제"
                        onClick={() => toggleColumn(name)}
                      >
                        <div className="card-row">
                          <span className="card-key">파일값</span>
                          <span className="metric-name" title={name}>{name}</span>
                          {on && <Check size={16} className="metric-check" />}
                        </div>
                        <div className="card-row edit">
                          <span className="card-key">표시값</span>
                          <input
                            className="metric-title-input"
                            value={columnTitles[name] ?? name}
                            placeholder="차트 제목"
                            title="차트 제목(표시값)"
                            onClick={(event) => event.stopPropagation()}
                            onMouseDown={(event) => event.stopPropagation()}
                            onChange={(event) => setColumnTitles((prev) => ({ ...prev, [name]: event.target.value }))}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="empty-hint">파일을 선택하면 비교 가능한 지표가 표시됩니다.</span>
              )}
            </div>

            <div className="form-section">
              <div className="section-head">
                <span className="step">2</span> 기간 · 축 · 옵션
              </div>
              <div className="fields-row">
                <div className="field">
                  <label>시작 (X축 기간)</label>
                  <select value={fromLabel} disabled={!rangeOptions.length} onChange={(event) => setFromLabel(event.target.value)}>
                    {rangeOptions.length ? (
                      rangeOptions.map((label) => (
                        <option key={`from-${label}`} value={label}>
                          {label}
                        </option>
                      ))
                    ) : (
                      <option value="">—</option>
                    )}
                  </select>
                </div>
                <div className="field">
                  <label>종료 (X축 기간)</label>
                  <select value={toLabel} disabled={!rangeOptions.length} onChange={(event) => setToLabel(event.target.value)}>
                    {rangeOptions.length ? (
                      rangeOptions.map((label) => (
                        <option key={`to-${label}`} value={label}>
                          {label}
                        </option>
                      ))
                    ) : (
                      <option value="">—</option>
                    )}
                  </select>
                </div>
                <div className="field soft" style={{ minWidth: 110 }}>
                  <label>Y축 최소</label>
                  <input type="number" placeholder="자동" value={yMinInput} onChange={(event) => setYMinInput(event.target.value)} />
                </div>
                <div className="field soft" style={{ minWidth: 110 }}>
                  <label>Y축 최대</label>
                  <input type="number" placeholder="자동" value={yMaxInput} onChange={(event) => setYMaxInput(event.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              className="secondary-button"
              disabled={!files.length || !selectedColumns.length}
              onClick={() => void preview()}
            >
              <Eye size={18} />
              미리보기
            </button>
            <button
              className="primary-button"
              disabled={!files.length || !selectedColumns.length || cmpBusy}
              onClick={() => void compareCompanies()}
            >
              <Send size={18} />
              {cmpBusy ? "생성 중..." : "게시 (대시보드에 저장)"}
            </button>
          </div>

          {previewCharts && (
            <div className="form-section" style={{ marginTop: 18 }}>
              <div className="section-head">
                <Eye size={16} /> 미리보기
                <span className="section-hint">게시 전 확인용 · 게시하면 지표 대시보드에 저장됩니다</span>
              </div>
              {previewCharts.length ? (
                <div className="chart-grid">
                  {previewCharts.map((chart) => (
                    <ChartCard chart={chart} key={chart.id} />
                  ))}
                </div>
              ) : (
                <span className="empty-hint">표시할 차트가 없습니다.</span>
              )}
            </div>
          )}
        </section>
      )}

      {activeTool === "dual" && <DualAxisTool api={api} onShowReports={onShowReports} />}

      {activeTool === "quantile" && (
        <QuantileForecastTool api={api} onShowReports={onShowReports} />
      )}

      {activeTool === "scatter" && <ScatterTool api={api} onShowReports={onShowReports} />}

      {message && <div className={message.includes("실패") ? "error" : "notice"}>{message}</div>}
    </div>
  );
}

function DualAxisTool({ api, onShowReports }: { api: Api | null; onShowReports?: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [fileLabels, setFileLabels] = useState<string[]>([]);
  const [columnOptions, setColumnOptions] = useState<string[]>([]);
  const [rangeOptions, setRangeOptions] = useState<string[]>([]);
  const [fromLabel, setFromLabel] = useState("");
  const [toLabel, setToLabel] = useState("");
  const [leftCol, setLeftCol] = useState("");
  const [rightCol, setRightCol] = useState("");
  const [leftTitle, setLeftTitle] = useState("");
  const [rightTitle, setRightTitle] = useState("");
  const [leftUnit, setLeftUnit] = useState("");
  const [rightUnit, setRightUnit] = useState("");
  const [leftMin, setLeftMin] = useState("");
  const [leftMax, setLeftMax] = useState("");
  const [rightMin, setRightMin] = useState("");
  const [rightMax, setRightMax] = useState("");
  const [reportTitle, setReportTitle] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [previewChart, setPreviewChart] = useState<ChartSpec | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (files.length < 1) {
      setColumnOptions([]);
      setRangeOptions([]);
      return;
    }
    void (async () => {
      try {
        const tables = await Promise.all(files.map(async (f) => await readWorkbookTable(await f.arrayBuffer())));
        const cols = (tables[0]?.columns ?? []).filter((name) => tables.every((t) => t.columns.includes(name)));
        const labels: string[] = [];
        for (const table of tables) {
          for (const label of table.labels) {
            if (!labels.includes(label)) labels.push(label);
          }
        }
        if (cancelled) return;
        setColumnOptions(cols);
        setLeftCol((prev) => (cols.includes(prev) ? prev : cols[0] ?? ""));
        setRightCol((prev) => (cols.includes(prev) ? prev : cols[1] ?? cols[0] ?? ""));
        setRangeOptions(labels);
        setFromLabel((prev) => (labels.includes(prev) ? prev : labels[0] ?? ""));
        setToLabel((prev) => (labels.includes(prev) ? prev : labels[labels.length - 1] ?? ""));
      } catch {
        if (!cancelled) {
          setColumnOptions([]);
          setRangeOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [files]);

  function options(): DualAxisOptions {
    const num = (s: string) => (s.trim() === "" ? undefined : Number(s));
    return {
      names: fileLabels,
      leftColumn: leftCol || undefined,
      rightColumn: rightCol || undefined,
      leftTitle,
      rightTitle,
      leftUnit,
      rightUnit,
      leftMin: Number.isFinite(num(leftMin)) ? num(leftMin) : undefined,
      leftMax: Number.isFinite(num(leftMax)) ? num(leftMax) : undefined,
      rightMin: Number.isFinite(num(rightMin)) ? num(rightMin) : undefined,
      rightMax: Number.isFinite(num(rightMax)) ? num(rightMax) : undefined,
      fromLabel: fromLabel || undefined,
      toLabel: toLabel || undefined,
      title: reportTitle,
      category
    };
  }

  const ready = files.length >= 1 && Boolean(leftCol) && Boolean(rightCol);

  async function preview() {
    if (!ready) return;
    setMessage("");
    try {
      const { chart } = await buildDualAxisChart(files, options());
      setPreviewChart(chart);
    } catch (caught) {
      setPreviewChart(null);
      setMessage(caught instanceof Error ? caught.message : "미리보기 생성에 실패했습니다.");
    }
  }

  async function publish() {
    if (!api || !ready) return;
    setBusy(true);
    setMessage("");
    try {
      await api.createDualAxis(files, options());
      setFiles([]);
      setFileLabels([]);
      setPreviewChart(null);
      setMessage("이중축 리포트를 생성했습니다. 지표 대시보드에서 확인하세요.");
      onShowReports?.();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "생성에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>이중축 시계열 비교</h2>
      <p style={{ color: "#607089", marginTop: 6, fontSize: 13 }}>
        단위·스케일이 다른 두 지표(예: 매출 / 증가율)를 <strong>좌축·우축</strong>에 나눠 한 그래프로 비교합니다.
        여러 회사를 올리면 회사별 색으로 좌축(실선)·우축(점선) 라인이 그려집니다.
      </p>

      <div className="form-grid">
        <div className="form-section">
          <div className="section-head">
            <span className="step">1</span> 회사 파일
            <span className="section-hint">엑셀 여러 개 선택 · 파일마다 범례 이름</span>
          </div>
          <div className="fields-row">
            <div className="field file-field">
              <label>회사 엑셀 파일</label>
              <label className={`file-input file-dropzone${files.length ? " has-files" : ""}`}>
                <span className="dropzone-top">
                  <UploadCloud size={20} />
                  {files.length ? `${files.length}개 파일 선택됨 — 다시 클릭해 변경` : "클릭하여 엑셀 파일 업로드"}
                </span>
                <span className="dropzone-hint">.xlsx · .xls · .csv (여러 개)</span>
                <input
                  type="file"
                  multiple
                  accept=".xlsx,.xls,.csv"
                  onChange={(event) => {
                    const picked = Array.from(event.target.files ?? []);
                    setFiles(picked);
                    setFileLabels(picked.map((f) => f.name.replace(/\.[^.]+$/, "")));
                    setPreviewChart(null);
                  }}
                />
              </label>
            </div>
            <div className="field key" style={{ minWidth: 200 }}>
              <label>리포트 제목</label>
              <input placeholder="예: 매출·증가율" value={reportTitle} onChange={(event) => setReportTitle(event.target.value)} />
              <span className="field-note">차트 제목으로 사용 · 비우면 자동 생성</span>
            </div>
            <div className="field" style={{ minWidth: 150 }}>
              <label>카테고리</label>
              <input placeholder="Report list 묶음명" value={category} onChange={(event) => setCategory(event.target.value)} />
            </div>
          </div>
          {files.length > 0 && (
            <div className="file-cards">
              {files.map((f, index) => (
                <div className="file-card" key={`${f.name}-${index}`}>
                  <div className="card-row">
                    <span className="card-key">파일명</span>
                    <span className="file-orig" title={f.name}>{f.name}</span>
                  </div>
                  <div className="card-row edit">
                    <span className="card-key">표시명</span>
                    <input
                      className="file-name-input"
                      value={fileLabels[index] ?? ""}
                      placeholder="범례 이름"
                      onChange={(event) =>
                        setFileLabels((prev) => prev.map((value, i) => (i === index ? event.target.value : value)))
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-section">
          <div className="section-head">
            <span className="step">2</span> 좌축 · 우축 지표
            <span className="section-hint">서로 다른 단위의 두 지표를 각 축에</span>
          </div>
          <div className="fields-row">
            <div className="field">
              <label>좌축 지표</label>
              <select value={leftCol} disabled={!columnOptions.length} onChange={(e) => setLeftCol(e.target.value)}>
                {columnOptions.length ? columnOptions.map((c) => <option key={`l-${c}`} value={c}>{c}</option>) : <option value="">—</option>}
              </select>
            </div>
            <div className="field key"><label>좌축 표시명</label><input placeholder="예: 매출" value={leftTitle} onChange={(e) => setLeftTitle(e.target.value)} /></div>
            <div className="field" style={{ minWidth: 100 }}><label>좌축 단위</label><input placeholder="예: 元" value={leftUnit} onChange={(e) => setLeftUnit(e.target.value)} /></div>
            <div className="field soft" style={{ minWidth: 100 }}><label>좌축 최소</label><input type="number" placeholder="자동" value={leftMin} onChange={(e) => setLeftMin(e.target.value)} /></div>
            <div className="field soft" style={{ minWidth: 100 }}><label>좌축 최대</label><input type="number" placeholder="자동" value={leftMax} onChange={(e) => setLeftMax(e.target.value)} /></div>
          </div>
          <div className="fields-row" style={{ marginTop: 10 }}>
            <div className="field">
              <label>우축 지표</label>
              <select value={rightCol} disabled={!columnOptions.length} onChange={(e) => setRightCol(e.target.value)}>
                {columnOptions.length ? columnOptions.map((c) => <option key={`r-${c}`} value={c}>{c}</option>) : <option value="">—</option>}
              </select>
            </div>
            <div className="field key"><label>우축 표시명</label><input placeholder="예: 증가율" value={rightTitle} onChange={(e) => setRightTitle(e.target.value)} /></div>
            <div className="field" style={{ minWidth: 100 }}><label>우축 단위</label><input placeholder="예: %" value={rightUnit} onChange={(e) => setRightUnit(e.target.value)} /></div>
            <div className="field soft" style={{ minWidth: 100 }}><label>우축 최소</label><input type="number" placeholder="자동" value={rightMin} onChange={(e) => setRightMin(e.target.value)} /></div>
            <div className="field soft" style={{ minWidth: 100 }}><label>우축 최대</label><input type="number" placeholder="자동" value={rightMax} onChange={(e) => setRightMax(e.target.value)} /></div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-head">
            <span className="step">3</span> 기간 (X축)
          </div>
          <div className="fields-row">
            <div className="field">
              <label>시작</label>
              <select value={fromLabel} disabled={!rangeOptions.length} onChange={(e) => setFromLabel(e.target.value)}>
                {rangeOptions.length ? rangeOptions.map((l) => <option key={`f-${l}`} value={l}>{l}</option>) : <option value="">—</option>}
              </select>
            </div>
            <div className="field">
              <label>종료</label>
              <select value={toLabel} disabled={!rangeOptions.length} onChange={(e) => setToLabel(e.target.value)}>
                {rangeOptions.length ? rangeOptions.map((l) => <option key={`t-${l}`} value={l}>{l}</option>) : <option value="">—</option>}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="secondary-button" disabled={!ready} onClick={() => void preview()}>
          <Eye size={18} />
          미리보기
        </button>
        <button className="primary-button" disabled={!ready || busy} onClick={() => void publish()}>
          <Send size={18} />
          {busy ? "생성 중..." : "게시 (대시보드에 저장)"}
        </button>
      </div>

      {previewChart && (
        <div className="form-section" style={{ marginTop: 18 }}>
          <div className="section-head">
            <Eye size={16} /> 미리보기
            <span className="section-hint">게시 전 확인용</span>
          </div>
          <div className="chart-grid">
            <ChartCard chart={previewChart} />
          </div>
        </div>
      )}

      {message && <div className={message.includes("실패") ? "error" : "notice"}>{message}</div>}
    </section>
  );
}

function QuantileForecastTool({ api, onShowReports }: { api: Api | null; onShowReports?: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [fileLabels, setFileLabels] = useState<string[]>([]);
  const [predictions, setPredictions] = useState<string[]>([]);
  const [tables, setTables] = useState<WorkbookTable[]>([]);
  const [columnOptions, setColumnOptions] = useState<string[]>([]);
  const [column, setColumn] = useState("");
  const [xAxisLabel, setXAxisLabel] = useState("");
  const [xUnit, setXUnit] = useState("");
  const [yAxisLabel, setYAxisLabel] = useState("");
  const [reportTitle, setReportTitle] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [previewChart, setPreviewChart] = useState<ChartSpec | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (files.length < 1) {
      setColumnOptions([]);
      setTables([]);
      return;
    }
    void (async () => {
      try {
        const read = await Promise.all(files.map(async (f) => await readWorkbookTable(await f.arrayBuffer())));
        // Only columns common (same header name) to ALL uploaded files.
        const options = (read[0]?.columns ?? []).filter((name) => read.every((t) => t.columns.includes(name)));
        if (cancelled) return;
        setTables(read);
        setColumnOptions(options);
        setColumn((prev) => (options.includes(prev) ? prev : options[0] ?? ""));
      } catch {
        if (!cancelled) {
          setColumnOptions([]);
          setTables([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [files]);

  function options(): QuantileForecastOptions {
    return {
      names: fileLabels,
      column: column || undefined,
      predictions,
      xAxisLabel,
      xUnit,
      yAxisLabel,
      title: reportTitle,
      category
    };
  }

  const ready = files.length >= 1 && Boolean(column);

  async function preview() {
    if (!ready) return;
    setMessage("");
    try {
      const { chart } = await buildQuantileForecastChart(files, options());
      setPreviewChart(chart);
    } catch (caught) {
      setPreviewChart(null);
      setMessage(caught instanceof Error ? caught.message : "미리보기 생성에 실패했습니다.");
    }
  }

  async function publish() {
    if (!api || !ready) return;
    setBusy(true);
    setMessage("");
    try {
      await api.createQuantileForecast(files, options());
      setFiles([]);
      setFileLabels([]);
      setPredictions([]);
      setPreviewChart(null);
      setMessage("분위·예측 리포트를 생성했습니다. 지표 대시보드에서 확인하세요.");
      onShowReports?.();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "생성에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>과거 분위 미래 예측</h2>
      <p style={{ color: "#607089", marginTop: 6, fontSize: 13 }}>
        파일별로 선택한 지표의 <strong>최신값이 과거 분포에서 차지하는 분위(%)= Y축</strong>, 파일마다 직접 입력한{" "}
        <strong>예측값 = X축</strong>인 산점도를 만듭니다. 점 1개 = 파일 1개입니다.
      </p>

      <div className="form-grid">
        <div className="form-section">
          <div className="section-head">
            <span className="step">1</span> 파일 · 예측값(X)
            <span className="section-hint">파일마다 표시명과 예측값을 입력</span>
          </div>
          <div className="fields-row">
            <div className="field file-field">
              <label>엑셀 파일</label>
              <label className={`file-input file-dropzone${files.length ? " has-files" : ""}`}>
                <span className="dropzone-top">
                  <UploadCloud size={20} />
                  {files.length ? `${files.length}개 파일 선택됨 — 다시 클릭해 변경` : "클릭하여 엑셀 파일 업로드"}
                </span>
                <span className="dropzone-hint">.xlsx · .xls · .csv (여러 개)</span>
                <input
                  type="file"
                  multiple
                  accept=".xlsx,.xls,.csv"
                  onChange={(event) => {
                    const picked = Array.from(event.target.files ?? []);
                    setFiles(picked);
                    setFileLabels(picked.map((f) => f.name.replace(/\.[^.]+$/, "")));
                    setPredictions(picked.map(() => ""));
                    setPreviewChart(null);
                  }}
                />
              </label>
            </div>
            <div className="field key" style={{ minWidth: 200 }}>
              <label>리포트 제목</label>
              <input placeholder="예: 산업구조 분위" value={reportTitle} onChange={(event) => setReportTitle(event.target.value)} />
              <span className="field-note">차트 제목으로 사용 · 비우면 자동 생성</span>
            </div>
            <div className="field" style={{ minWidth: 150 }}>
              <label>카테고리</label>
              <input placeholder="Report list 묶음명" value={category} onChange={(event) => setCategory(event.target.value)} />
            </div>
          </div>
          {files.length > 0 && (
            <div className="file-cards">
              {files.map((f, index) => {
                const obs = tables[index]?.labels.length ?? 0;
                const colIndex = (tables[0]?.columns ?? []).indexOf(column);
                const colVals = column && tables[index] ? columnValues(tables[index], column, colIndex) : undefined;
                const quantile = colVals && colVals.length ? latestPercentile(colVals) : null;
                const current = colVals && colVals.length ? colVals[colVals.length - 1] : null;
                return (
                  <div className="file-card" key={`${f.name}-${index}`}>
                    <div className="card-row">
                      <span className="card-key">파일명</span>
                      <span className="file-orig" title={f.name}>{f.name}</span>
                    </div>
                    <div className="card-row">
                      <span className="card-key">관측치</span>
                      <span className="file-orig">{obs}개</span>
                    </div>
                    <div className="card-row edit">
                      <span className="card-key">표시명</span>
                      <input
                        className="file-name-input"
                        value={fileLabels[index] ?? ""}
                        placeholder="점 이름"
                        onChange={(event) =>
                          setFileLabels((prev) => prev.map((value, i) => (i === index ? event.target.value : value)))
                        }
                      />
                    </div>
                    <div className="card-row edit">
                      <span className="card-key">예측값</span>
                      <input
                        className="file-name-input"
                        type="number"
                        value={predictions[index] ?? ""}
                        placeholder="X축 예측값"
                        onChange={(event) =>
                          setPredictions((prev) => prev.map((value, i) => (i === index ? event.target.value : value)))
                        }
                      />
                    </div>
                    <div className="card-row">
                      <span className="card-key">분위값</span>
                      <span className="file-orig" style={{ color: quantile === null ? "#94a3b8" : "#1d4ed8", fontWeight: 600 }}>
                        {quantile === null ? "열 선택 시 표시" : `${quantile.toFixed(1)}%`}
                      </span>
                    </div>
                    <div className="card-row">
                      <span className="card-key">현재값</span>
                      <span className="file-orig" style={{ color: current === null ? "#94a3b8" : "#0f766e", fontWeight: 600 }}>
                        {current === null ? "열 선택 시 표시" : current.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="form-section">
          <div className="section-head">
            <span className="step">2</span> 분위 지표 · 축 라벨
          </div>
          <div className="fields-row">
            <div className="field">
              <label>분위 계산 열(Y)</label>
              <select value={column} disabled={!columnOptions.length} onChange={(e) => setColumn(e.target.value)}>
                {columnOptions.length ? columnOptions.map((c) => <option key={c} value={c}>{c}</option>) : <option value="">—</option>}
              </select>
            </div>
            <div className="field key"><label>X축 라벨</label><input placeholder="예: EPS growth" value={xAxisLabel} onChange={(e) => setXAxisLabel(e.target.value)} /></div>
            <div className="field" style={{ minWidth: 100 }}><label>X축 단위</label><input placeholder="예: %" value={xUnit} onChange={(e) => setXUnit(e.target.value)} /></div>
            <div className="field key"><label>Y축 라벨</label><input placeholder="예: P/E 분위(%)" value={yAxisLabel} onChange={(e) => setYAxisLabel(e.target.value)} /></div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="secondary-button" disabled={!ready} onClick={() => void preview()}>
          <Eye size={18} />
          미리보기
        </button>
        <button className="primary-button" disabled={!ready || busy} onClick={() => void publish()}>
          <Send size={18} />
          {busy ? "생성 중..." : "게시 (대시보드에 저장)"}
        </button>
      </div>

      {previewChart && (
        <div className="form-section" style={{ marginTop: 18 }}>
          <div className="section-head">
            <Eye size={16} /> 미리보기
            <span className="section-hint">게시 전 확인용</span>
          </div>
          <div className="chart-grid">
            <ChartCard chart={previewChart} />
          </div>
        </div>
      )}

      {message && <div className={message.includes("실패") ? "error" : "notice"}>{message}</div>}
    </section>
  );
}

// 산업 산점도: one Excel (类别·行业 + numeric metric columns) → a category-
// coloured scatter. The admin picks which numeric columns are X and Y, so it
// serves momentum (涨跌幅 vs 涨跌幅), crowding (相对位置 vs 변화), and any future
// two-metric industry chart. Re-uploading the same TITLE overwrites that chart.
function ScatterTool({ api, onShowReports }: { api: Api | null; onShowReports?: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [xColumn, setXColumn] = useState("");
  const [yColumn, setYColumn] = useState("");
  const [reportTitle, setReportTitle] = useState("");
  const [category, setCategory] = useState("");
  const [xAxisLabel, setXAxisLabel] = useState("");
  const [yAxisLabel, setYAxisLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [previewChart, setPreviewChart] = useState<ChartSpec | null>(null);

  // On upload, read the numeric columns and default the X/Y pickers.
  useEffect(() => {
    let cancelled = false;
    if (!files.length) {
      setColumns([]);
      return;
    }
    void (async () => {
      try {
        const table = await parseScatterRows(await files[0].arrayBuffer());
        if (cancelled) return;
        setColumns(table.numericColumns);
        const def = defaultScatterAxes(table.numericColumns);
        setXColumn(def.x);
        setYColumn(def.y);
      } catch {
        if (!cancelled) setColumns([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [files]);

  function options(): ScatterOptions {
    return { xColumn, yColumn, title: reportTitle, category, xAxisLabel, yAxisLabel };
  }

  const ready = files.length >= 1 && Boolean(xColumn) && Boolean(yColumn);

  async function preview() {
    if (!ready) return;
    setMessage("");
    try {
      const { chart } = await buildScatterChart(files[0], options());
      setPreviewChart(chart);
    } catch (caught) {
      setPreviewChart(null);
      setMessage(caught instanceof Error ? caught.message : "미리보기 생성에 실패했습니다.");
    }
  }

  async function publish() {
    if (!api || !ready) return;
    setBusy(true);
    setMessage("");
    try {
      await api.createScatter(files, options());
      setFiles([]);
      setPreviewChart(null);
      setMessage("산업 산점도를 게시했습니다 (같은 제목은 갱신). 지표 대시보드에서 확인하세요.");
      onShowReports?.();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "게시에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>산업 산점도</h2>
      <p style={{ color: "#607089", marginTop: 6, fontSize: 13 }}>
        분류·산업별 <strong>두 지표(X·Y) 산점도</strong>를 만듭니다 — 점 1개 = 산업 1개, 색 = 대분류
        (TMT·制造·消费·周期·金融地产). 엑셀에서 <strong>X·Y로 쓸 숫자 열을 직접 선택</strong>합니다
        (모멘텀: 近20日·近5日涨跌幅 / 拥挤도: 相对位置·相对位置变化). 같은 제목으로 다시 올리면 그 차트가 갱신됩니다.
      </p>

      <div className="form-grid">
        <div className="form-section">
          <div className="section-head">
            <span className="step">1</span> 엑셀 · X/Y 열 · 옵션
            <span className="section-hint">엑셀 1개 업로드 → X·Y 열 선택 → (선택) 제목·축 이름</span>
          </div>
          <div className="fields-row">
            <div className="field file-field">
              <label>엑셀 파일</label>
              <label className={`file-input file-dropzone${files.length ? " has-files" : ""}`}>
                <span className="dropzone-top">
                  <UploadCloud size={20} />
                  {files.length ? `${files[0].name} — 다시 클릭해 변경` : "클릭하여 엑셀 파일 업로드"}
                </span>
                <span className="dropzone-hint">.xlsx · .xls · .csv (1개)</span>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(event) => {
                    const picked = Array.from(event.target.files ?? []);
                    setFiles(picked.slice(0, 1));
                    setPreviewChart(null);
                  }}
                />
              </label>
            </div>
            <div className="field key" style={{ minWidth: 200 }}>
              <label>리포트 제목</label>
              <input placeholder="예: 1급산업 월별" value={reportTitle} onChange={(event) => setReportTitle(event.target.value)} />
              <span className="field-note">같은 제목 = 갱신 슬롯 · 비우면 "산업 산점도"</span>
            </div>
            <div className="field" style={{ minWidth: 160 }}>
              <label>카테고리</label>
              <input placeholder="예: 1급산업 분포" value={category} onChange={(event) => setCategory(event.target.value)} />
              <span className="field-note">같은 카테고리끼리 Report list에서 묶임</span>
            </div>
          </div>
          {columns.length > 0 && (
            <div className="fields-row">
              <div className="field">
                <label>X축 열</label>
                <select value={xColumn} onChange={(event) => setXColumn(event.target.value)}>
                  {columns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Y축 열</label>
                <select value={yColumn} onChange={(event) => setYColumn(event.target.value)}>
                  {columns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="fields-row">
            <div className="field">
              <label>X축 이름</label>
              <input placeholder="비우면 X 열 이름 사용" value={xAxisLabel} onChange={(event) => setXAxisLabel(event.target.value)} />
            </div>
            <div className="field">
              <label>Y축 이름</label>
              <input placeholder="비우면 Y 열 이름 사용" value={yAxisLabel} onChange={(event) => setYAxisLabel(event.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="secondary-button" disabled={!ready} onClick={() => void preview()}>
          <Eye size={18} />
          미리보기
        </button>
        <button className="primary-button" disabled={!ready || busy} onClick={() => void publish()}>
          <Send size={18} />
          {busy ? "게시 중..." : "게시 (같은 제목은 갱신)"}
        </button>
      </div>

      {previewChart && (
        <div className="form-section" style={{ marginTop: 18 }}>
          <div className="section-head">
            <Eye size={16} /> 미리보기
            <span className="section-hint">게시하면 지표 대시보드의 같은 제목 차트를 대체합니다</span>
          </div>
          <div className="chart-grid">
            <ChartCard chart={previewChart} />
          </div>
        </div>
      )}

      {message && <div className={message.includes("실패") ? "error" : "notice"}>{message}</div>}
    </section>
  );
}

function ReportsPage({ api, user }: { api: Api | null; user: AuthUser }) {
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [message, setMessage] = useState("");
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Working copy while editing a published report's display fields in place.
  const [editing, setEditing] = useState<ReportDetail | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  // Report pending deletion — drives the confirmation dialog (delete is permanent).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Category groups (admin-assigned at publish/edit time). `null` = not yet
  // initialised from the data; default = all collapsed except the group holding
  // the auto-selected first report.
  const [collapsedCats, setCollapsedCats] = useState<Set<string> | null>(null);
  const chartGridRef = useRef<HTMLDivElement>(null);
  const canUseApi = Boolean(config.apiBaseUrl) || config.localMock;
  // Reorder/delete/edit are admin-only.
  const canEdit = user.roles.includes("Admin");

  function toggleReportGroup(cat: string) {
    setCollapsedCats((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  function startEdit() {
    if (!detail) return;
    setMessage("");
    setEditing(structuredClone(detail));
  }

  async function saveEdit() {
    if (!editing || !api) return;
    setEditBusy(true);
    try {
      await api.updateReport(editing.report_id, {
        title: editing.title,
        charts: editing.charts,
        category: editing.category ?? ""
      });
      const category = editing.category?.trim() || undefined;
      setReports((prev) =>
        prev.map((report) =>
          report.report_id === editing.report_id ? { ...report, title: editing.title, category } : report
        )
      );
      setDetail(editing);
      setEditing(null);
      setMessage("리포트를 수정해 다시 게시했습니다.");
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "수정에 실패했습니다.");
    } finally {
      setEditBusy(false);
    }
  }

  function moveReport(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || to >= reports.length) return;
    const next = [...reports];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setReports(next);
    void api?.reorderReports?.(next.map((report) => report.report_id));
  }

  async function removeReport(id: string) {
    try {
      await api?.deleteReport?.(id);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "삭제에 실패했습니다.");
      return;
    }
    const remaining = reports.filter((report) => report.report_id !== id);
    setReports(remaining);
    if (selectedId === id) {
      const nextSelected = remaining[0]?.report_id ?? "";
      setSelectedId(nextSelected);
      if (!nextSelected) setDetail(null);
    }
  }

  async function confirmDelete() {
    const id = confirmDeleteId;
    if (!id) return;
    setConfirmDeleteId(null);
    await removeReport(id);
  }

  useEffect(() => {
    if (!api || !canUseApi) return;
    api
      .listReports({ includeDrafts: user.roles.includes("Admin") })
      .then(({ items, nextCursor: cursor }) => {
        setMessage("");
        setReports(items);
        setNextCursor(cursor);
        setSelectedId(items[0]?.report_id ?? "");
        if (items.length === 0) setDetail(null);
        // Default: collapse every category except the auto-selected report's.
        const firstCat = items[0]?.category?.trim() || "미분류";
        const cats = new Set(items.map((r) => r.category?.trim() || "미분류"));
        cats.delete(firstCat);
        setCollapsedCats((prev) => prev ?? cats);
      })
      .catch((caught) => setMessage(caught instanceof Error ? caught.message : "Unable to load reports."));
  }, [api, canUseApi, user.roles]);

  async function loadMore() {
    if (!api || !canUseApi || !nextCursor) return;
    try {
      const { items, nextCursor: cursor } = await api.listReports({
        includeDrafts: user.roles.includes("Admin"),
        cursor: nextCursor
      });
      setReports((prev) => [...prev, ...items]);
      setNextCursor(cursor);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Unable to load more reports.");
    }
  }

  useEffect(() => {
    if (!api || !canUseApi) return;

    if (!selectedId) {
      setDetail(null);
      return;
    }

    api
      .getReport(selectedId)
      .then((report) => {
        setMessage("");
        setDetail(report);
      })
      .catch((caught) => setMessage(caught instanceof Error ? caught.message : "Unable to load report."));
  }, [api, canUseApi, selectedId]);

  // Save the selected report's charts as a PNG. Rasterizes the whole chart grid
  // (titles, legends, SVG line/scatter charts AND the HTML/CSS bar charts) so
  // the image matches exactly what's on screen. Runs entirely client-side, so it
  // works in local mock and dev/prod alike.
  async function download() {
    const node = chartGridRef.current;
    if (!detail || !node) return;
    try {
      // html-to-image loads on demand — only admins/users who export PNGs pay
      // for it, not every page load.
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
        cacheBust: true
      });
      const anchor = document.createElement("a");
      anchor.href = dataUrl;
      anchor.download = `${(detail.title || "report").replace(/[\\/:*?"<>|]+/g, "_")}.png`;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "이미지 다운로드에 실패했습니다.");
    }
  }

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <h1>지표 대시보드</h1>
          <p>게시된 지표 차트를 조회하고 다운로드합니다.</p>
        </div>
        {!editing && (
          <div className="login-actions">
            {canEdit && (
              <button
                className="secondary-button"
                disabled={!detail}
                onClick={startEdit}
                title="제목·축 라벨 등 표시 정보를 편집해 다시 게시"
              >
                <Pencil size={18} />
                편집
              </button>
            )}
            <button
              className="secondary-button"
              disabled={!detail}
              onClick={() => void download()}
              title="현재 리포트를 PNG 이미지로 저장"
            >
              <Download size={18} />
              Download
            </button>
          </div>
        )}
      </header>
      {!canUseApi && (
        <div className="notice">Frontend deployment is ready. Backend API URL is pending CDK deployment and GitHub variable setup.</div>
      )}
      {message && <div className={message.includes("실패") ? "error" : "notice"}>{message}</div>}
      {editing ? (
        <ReportEditor
          value={editing}
          busy={editBusy}
          onChange={setEditing}
          onSave={() => void saveEdit()}
          onCancel={() => setEditing(null)}
        />
      ) : (
      <section className="report-layout">
        <div className="panel report-list">
          <h2>Report list</h2>
          {canEdit && reports.length > 0 && (
            <p className="empty-hint" style={{ marginBottom: 4 }}>카테고리 클릭으로 펼침 · 드래그로 순서 변경 · 휴지통으로 삭제</p>
          )}
          {groupReportsByCategory(reports).map(([cat, list], gi) => (
            <div className="industry-group" key={cat}>
              <button
                type="button"
                className={collapsedCats?.has(cat) ? "industry-group-head collapsed" : "industry-group-head"}
                style={{ ["--cat-hue" as string]: String(categoryHue(cat, gi)) }}
                onClick={() => toggleReportGroup(cat)}
              >
                {collapsedCats?.has(cat) ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                {cat} <span>{list.length}</span>
              </button>
              {!collapsedCats?.has(cat) &&
                list.map((report) => {
                  const index = reports.indexOf(report);
                  return (
                    <div
                      key={report.report_id}
                      className={
                        "report-item" +
                        (dragIndex === index ? " dragging" : "") +
                        (dragOverIndex === index && dragIndex !== null && dragIndex !== index ? " drag-over" : "")
                      }
                      draggable={canEdit}
                      onDragStart={canEdit ? () => setDragIndex(index) : undefined}
                      onDragOver={
                        canEdit
                          ? (event) => {
                              event.preventDefault();
                              setDragOverIndex(index);
                            }
                          : undefined
                      }
                      onDrop={
                        canEdit
                          ? (event) => {
                              event.preventDefault();
                              if (dragIndex !== null) moveReport(dragIndex, index);
                              setDragIndex(null);
                              setDragOverIndex(null);
                            }
                          : undefined
                      }
                      onDragEnd={
                        canEdit
                          ? () => {
                              setDragIndex(null);
                              setDragOverIndex(null);
                            }
                          : undefined
                      }
                    >
                      {canEdit && (
                        <span className="report-grip" title="드래그하여 순서 변경">
                          <GripVertical size={16} />
                        </span>
                      )}
                      <button
                        className={selectedId === report.report_id ? "report-link active" : "report-link"}
                        onClick={() => setSelectedId(report.report_id)}
                      >
                        <strong>
                          <span className="source-icon" title={report.source ?? ""}>
                            {sourceIcon(report.source)}
                          </span>
                          {report.title}
                        </strong>
                      </button>
                      {canEdit && (
                        <button
                          className="report-delete"
                          title="리포트 삭제"
                          onClick={() => setConfirmDeleteId(report.report_id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          ))}
          {!reports.length && <EmptyState text="No published reports yet." />}
          {nextCursor && (
            <button className="secondary-button load-more" onClick={() => void loadMore()}>
              더 보기
            </button>
          )}
        </div>
        <div className="chart-grid" ref={chartGridRef}>
          {detail ? (
            detail.charts.map((chart) => <ChartCard chart={chart} key={chart.id} showRaw={canEdit} />)
          ) : (
            <EmptyState text="Select a report to view its charts." />
          )}
        </div>
      </section>
      )}

      {confirmDeleteId && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteId(null)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3>리포트를 삭제할까요?</h3>
            <p>
              <strong>{reports.find((report) => report.report_id === confirmDeleteId)?.title ?? "이 리포트"}</strong>
              {" 를 삭제합니다. 차트 데이터와 다운로드 파일이 함께 제거되며 되돌릴 수 없습니다."}
            </p>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setConfirmDeleteId(null)}>
                취소
              </button>
              <button className="danger-button" onClick={() => void confirmDelete()}>
                <Trash2 size={16} />
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Edits a published report's surrounding display fields — report title, and per
// chart: title, units, axis labels/units, axis min/max, and legend (series/point)
// names — then re-publishes in place. The chart *values* are never touched, so no
// raw re-upload is needed. A live preview shows the result before saving.
function ReportEditor({
  value,
  busy,
  onChange,
  onSave,
  onCancel
}: {
  value: ReportDetail;
  busy: boolean;
  onChange: (next: ReportDetail) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  function patchChart(index: number, patch: Partial<ChartSpec>) {
    onChange({ ...value, charts: value.charts.map((chart, i) => (i === index ? { ...chart, ...patch } : chart)) });
  }
  function patchSeriesName(chartIndex: number, seriesIndex: number, name: string) {
    patchChart(chartIndex, {
      series: value.charts[chartIndex].series.map((s, j) => (j === seriesIndex ? { ...s, name } : s))
    });
  }
  function patchPointLabel(chartIndex: number, pointIndex: number, label: string) {
    const points = value.charts[chartIndex].points;
    if (!points) return;
    patchChart(chartIndex, { points: points.map((p, j) => (j === pointIndex ? { ...p, label } : p)) });
  }
  // Number fields: blank clears the value (auto), otherwise parse; ignore NaN.
  const numPatch = (key: keyof ChartSpec) => (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === "") return { [key]: undefined } as Partial<ChartSpec>;
    const n = Number(trimmed);
    return Number.isFinite(n) ? ({ [key]: n } as Partial<ChartSpec>) : {};
  };

  return (
    <section className="report-layout report-editor">
      <div className="panel report-edit-form">
        <div className="section-head">
          <Pencil size={16} /> 리포트 편집
          <span className="section-hint">표시 정보만 수정 · 원본 재업로드 불필요</span>
        </div>

        <div className="fields-row">
          <div className="field key" style={{ minWidth: 200 }}>
            <label>리포트 제목</label>
            <input value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} />
          </div>
          <div className="field" style={{ minWidth: 150 }}>
            <label>카테고리</label>
            <input
              placeholder="Report list 묶음명 · 비우면 미분류"
              value={value.category ?? ""}
              onChange={(e) => onChange({ ...value, category: e.target.value })}
            />
          </div>
        </div>

        {value.charts.map((chart, ci) => {
          const isScatter = chart.kind === "scatter" || Boolean(chart.points?.length);
          return (
            <div className="edit-chart" key={chart.id}>
              <div className="edit-chart-head">차트 {value.charts.length > 1 ? ci + 1 : ""}</div>
              <div className="fields-row">
                <div className="field key" style={{ minWidth: 200 }}>
                  <label>차트 제목</label>
                  <input value={chart.title ?? ""} onChange={(e) => patchChart(ci, { title: e.target.value })} />
                </div>
                <div className="field" style={{ minWidth: 90 }}>
                  <label>단위</label>
                  <input value={chart.unit ?? ""} onChange={(e) => patchChart(ci, { unit: e.target.value })} />
                </div>
                {isScatter && (
                  <div className="field key" style={{ minWidth: 140 }}>
                    <label>X축 라벨</label>
                    <input value={chart.xAxisLabel ?? ""} onChange={(e) => patchChart(ci, { xAxisLabel: e.target.value })} />
                  </div>
                )}
                {isScatter && (
                  <div className="field" style={{ minWidth: 80 }}>
                    <label>X축 단위</label>
                    <input value={chart.xUnit ?? ""} onChange={(e) => patchChart(ci, { xUnit: e.target.value })} />
                  </div>
                )}
                <div className="field key" style={{ minWidth: 140 }}>
                  <label>Y축 라벨</label>
                  <input value={chart.yAxisLabel ?? ""} onChange={(e) => patchChart(ci, { yAxisLabel: e.target.value })} />
                </div>
                <div className="field soft" style={{ minWidth: 90 }}>
                  <label>Y축 최소</label>
                  <input
                    type="number"
                    placeholder="자동"
                    value={chart.yMin ?? ""}
                    onChange={(e) => patchChart(ci, numPatch("yMin")(e.target.value))}
                  />
                </div>
                <div className="field soft" style={{ minWidth: 90 }}>
                  <label>Y축 최대</label>
                  <input
                    type="number"
                    placeholder="자동"
                    value={chart.yMax ?? ""}
                    onChange={(e) => patchChart(ci, numPatch("yMax")(e.target.value))}
                  />
                </div>
                {chart.dualAxis && (
                  <>
                    <div className="field" style={{ minWidth: 90 }}>
                      <label>우축 단위</label>
                      <input value={chart.rightUnit ?? ""} onChange={(e) => patchChart(ci, { rightUnit: e.target.value })} />
                    </div>
                    <div className="field soft" style={{ minWidth: 90 }}>
                      <label>우축 최소</label>
                      <input
                        type="number"
                        placeholder="자동"
                        value={chart.rightMin ?? ""}
                        onChange={(e) => patchChart(ci, numPatch("rightMin")(e.target.value))}
                      />
                    </div>
                    <div className="field soft" style={{ minWidth: 90 }}>
                      <label>우축 최대</label>
                      <input
                        type="number"
                        placeholder="자동"
                        value={chart.rightMax ?? ""}
                        onChange={(e) => patchChart(ci, numPatch("rightMax")(e.target.value))}
                      />
                    </div>
                  </>
                )}
              </div>

              {isScatter && chart.points?.length ? (
                <div className="edit-names">
                  <span className="card-key">점 이름</span>
                  <div className="fields-row">
                    {chart.points.map((point, pi) => (
                      <input
                        key={pi}
                        className="file-name-input"
                        value={point.label}
                        onChange={(e) => patchPointLabel(ci, pi, e.target.value)}
                      />
                    ))}
                  </div>
                </div>
              ) : chart.series.length ? (
                <div className="edit-names">
                  <span className="card-key">범례명</span>
                  <div className="fields-row">
                    {chart.series.map((s, si) => (
                      <input
                        key={si}
                        className="file-name-input"
                        value={s.name}
                        onChange={(e) => patchSeriesName(ci, si, e.target.value)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="form-actions">
          <button className="secondary-button" onClick={onCancel} disabled={busy}>
            취소
          </button>
          <button className="primary-button" onClick={onSave} disabled={busy || !value.title.trim()}>
            <Send size={18} />
            {busy ? "저장 중..." : "다시 게시"}
          </button>
        </div>
      </div>

      <div className="chart-grid">
        {value.charts.map((chart) => (
          <ChartCard chart={chart} key={chart.id} />
        ))}
      </div>
    </section>
  );
}

// Industry datasets: upload a weekly Excel; the system accumulates the time
// series and (re)generates one report per industry from the FULL history.
// Freshness: stale if the latest data week is older than ~10 days.
function isStale(lastWeek?: string): boolean {
  if (!lastWeek) return true;
  const days = (Date.now() - new Date(lastWeek).getTime()) / 86400000;
  return !Number.isFinite(days) || days > 10;
}

function groupByCategory(
  items: IndustrySummary[],
  order: string[] = []
): Array<[string, IndustrySummary[]]> {
  const groups = new Map<string, IndustrySummary[]>();
  for (const ind of items) {
    const key = ind.category?.trim() || "미분류";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(ind);
  }
  // Categories appear in the admin-set order first; any not in the order list
  // fall back to alphabetical at the end.
  const rank = (cat: string) => {
    const i = order.indexOf(cat);
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };
  return [...groups.entries()].sort((a, b) => {
    const ra = rank(a[0]);
    const rb = rank(b[0]);
    return ra !== rb ? ra - rb : a[0].localeCompare(b[0]);
  });
}

interface UploadRow {
  file: File;
  name: string;
  category: string;
  status?: "ok" | "error";
  error?: string;
}

// "TMT 20260403.xlsx" -> "TMT" (drop extension and a trailing date stamp).
function defaultIndustryName(filename: string): string {
  return filename.replace(/\.[^.]+$/, "").replace(/[\s_.-]*\d{6,8}\s*$/, "").trim() || filename;
}

// Admin-only industry management page (its own nav section). Bulk-upload weekly
// data, organize 100+ industries by category, see freshness, open dashboards.
function IndustryManager({ api, onShowDashboards }: { api: Api | null; onShowDashboards?: () => void }) {
  const [industries, setIndustries] = useState<IndustrySummary[]>([]);
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [bulkCategory, setBulkCategory] = useState("");
  const [progress, setProgress] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string> | null>(null);
  const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
  const [dragCat, setDragCat] = useState<string | null>(null);
  const [dragOverCat, setDragOverCat] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteCat, setConfirmDeleteCat] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("");

  function toggleGroup(cat: string) {
    setCollapsed((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  async function refresh() {
    if (!api) return;
    try {
      const items = await api.listIndustries();
      setIndustries(items);
      const cats = new Set(items.map((i) => i.category?.trim() || "미분류"));
      setCollapsed((prev) => prev ?? cats); // default: all collapsed
    } catch {
      /* list may be empty before first upload */
    }
    api.getCategoryOrder().then(setCategoryOrder).catch(() => undefined);
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Drag a category header to reorder; persist the new order (admin only).
  async function reorderCategories(from: string, to: string) {
    if (from === to) return;
    const current = groupByCategory(industries, categoryOrder).map(([cat]) => cat);
    const next = current.filter((c) => c !== from);
    const at = next.indexOf(to);
    next.splice(at < 0 ? next.length : at, 0, from);
    setCategoryOrder(next); // optimistic
    try {
      await api?.setCategoryOrder(next);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "순서 저장에 실패했습니다.");
    }
  }

  async function deleteCategory(cat: string) {
    if (!api) return;
    try {
      const n = await api.deleteCategory(cat);
      setConfirmDeleteCat(null);
      await refresh();
      setMessage(`'${cat}' 카테고리와 그 안의 ${n}개 산업을 삭제했습니다.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "카테고리 삭제에 실패했습니다.");
    }
  }

  function pickFiles(fileList: FileList | null) {
    const picked = Array.from(fileList ?? []);
    setRows(picked.map((f) => ({ file: f, name: defaultIndustryName(f.name), category: bulkCategory })));
    setProgress("");
    setMessage("");
  }

  function patchRow(index: number, patch: Partial<UploadRow>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function applyBulkCategory() {
    setRows((prev) => prev.map((r) => ({ ...r, category: bulkCategory })));
  }

  // Upload each file in turn, recording per-file status. Dataset identity is
  // name + category: a row whose name AND category match an existing industry
  // accumulates into it; the same name under a DIFFERENT category becomes its
  // own dataset. An empty category matches by name alone (when unique), so the
  // routine weekly re-upload doesn't need the category re-typed.
  async function uploadAll() {
    if (!api || !rows.length) return;
    setBusy(true);
    setMessage("");
    let ok = 0;
    let fail = 0;
    const next: UploadRow[] = rows.map((r) => ({ ...r, status: undefined, error: undefined }));
    for (let i = 0; i < next.length; i += 1) {
      const row = next[i];
      try {
        if (!row.name.trim()) throw new Error("이름 없음");
        const parsed = await parseIndustryWorkbook(await row.file.arrayBuffer());
        if (!parsed.charts.length) throw new Error("데이터 블록 없음");
        const nameKey = row.name.trim();
        const catKey = row.category.trim();
        const sameName = industries.filter((ind) => ind.name === nameKey);
        const target =
          sameName.find((ind) => (ind.category?.trim() ?? "") === catKey) ??
          (catKey === "" && sameName.length === 1 ? sameName[0] : undefined);
        await api.ingestIndustry({
          industryId: target?.industry_id,
          name: nameKey,
          category: catKey || undefined,
          charts: parsed.charts,
          points: parsed.points
        });
        next[i] = { ...row, status: "ok" };
        ok += 1;
      } catch (caught) {
        next[i] = { ...row, status: "error", error: caught instanceof Error ? caught.message : "실패" };
        fail += 1;
      }
      setRows([...next]);
      setProgress(`${ok + fail}/${rows.length}`);
    }
    setBusy(false);
    await refresh();
    setMessage(`업로드 완료 — 성공 ${ok}개${fail ? `, 실패 ${fail}개` : ""}. 산업 대시보드에서 확인하세요.`);
  }

  async function remove(id: string) {
    setConfirmDeleteId(null);
    if (!api) return;
    try {
      await api.deleteIndustry(id);
      await refresh();
      setMessage("산업 데이터셋을 삭제했습니다.");
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "삭제에 실패했습니다.");
    }
  }

  function openEdit(ind: IndustrySummary) {
    setEditId(ind.industry_id);
    setEditName(ind.name);
    setEditCategory(ind.category ?? "");
  }

  async function saveEdit() {
    if (!api || !editId) return;
    if (!editName.trim()) {
      setMessage("산업 이름을 입력하세요.");
      return;
    }
    try {
      await api.updateIndustry(editId, { name: editName.trim(), category: editCategory.trim() });
      setEditId(null);
      await refresh();
      setMessage("산업 정보를 수정했습니다.");
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "수정에 실패했습니다.");
    }
  }

  const q = search.trim().toLowerCase();
  const allCategories = [...new Set(industries.map((ind) => ind.category?.trim() || "미분류"))].sort((a, b) =>
    a.localeCompare(b)
  );
  const filtered = industries.filter((ind) => {
    const cat = ind.category?.trim() || "미분류";
    if (catFilter && cat !== catFilter) return false;
    return !q || ind.name.toLowerCase().includes(q) || cat.toLowerCase().includes(q);
  });
  const groups = groupByCategory(filtered, categoryOrder);
  const isCollapsed = (cat: string) => collapsed?.has(cat) ?? true;
  const allCollapsed = groups.length > 0 && groups.every(([cat]) => isCollapsed(cat));
  const freshCount = industries.filter((i) => !isStale(i.last_week)).length;
  const categories = [...new Set(industries.map((i) => i.category).filter(Boolean))] as string[];

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <h1>산업 분석</h1>
          <p>매주 산업 엑셀을 올리면 데이터가 누적되고, 전체 히스토리로 대시보드가 자동 갱신됩니다 (관리자 전용).</p>
        </div>
      </header>

      <section className="panel">
        <div className="section-head">
          <span className="step">1</span> 주간 데이터 업로드 (여러 개 동시)
          <span className="section-hint">
            같은 이름+카테고리면 기존 산업에 누적 · 이름이 같아도 카테고리가 다르면 별도 산업 생성
          </span>
        </div>
        <datalist id="industry-categories">
          {categories.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>

        <label className={`file-input file-dropzone${rows.length ? " has-files" : ""}`}>
          <span className="dropzone-top">
            <UploadCloud size={20} />
            {rows.length ? `${rows.length}개 파일 선택됨 — 다시 클릭해 변경` : "클릭하여 주간 엑셀 여러 개 선택"}
          </span>
          <span className="dropzone-hint">.xlsx · .xls · .csv · 파일명에서 산업명을 자동 추출합니다</span>
          <input
            type="file"
            multiple
            accept=".xlsx,.xls,.csv"
            onChange={(event) => pickFiles(event.target.files)}
          />
        </label>

        {rows.length > 0 && (
          <>
            <div className="bulk-cat-row">
              <input
                placeholder="카테고리 일괄 입력 (예: 핫이슈)"
                value={bulkCategory}
                onChange={(event) => setBulkCategory(event.target.value)}
                list="industry-categories"
              />
              <button type="button" className="secondary-button" onClick={applyBulkCategory}>
                모든 행에 적용
              </button>
            </div>

            <div className="upload-table-scroll">
              <table className="upload-table">
                <thead>
                  <tr>
                    <th>파일</th>
                    <th>산업 이름</th>
                    <th>카테고리 (섹터)</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={`${row.file.name}-${i}`}>
                      <td className="upload-file">{row.file.name}</td>
                      <td>
                        <input value={row.name} onChange={(event) => patchRow(i, { name: event.target.value })} />
                      </td>
                      <td>
                        <input
                          value={row.category}
                          placeholder="미분류"
                          list="industry-categories"
                          onChange={(event) => patchRow(i, { category: event.target.value })}
                        />
                      </td>
                      <td className="upload-status">
                        {row.status === "ok" ? (
                          <span className="badge fresh">완료</span>
                        ) : row.status === "error" ? (
                          <span className="badge stale" title={row.error}>
                            실패
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="form-actions">
              {progress && <span className="upload-progress">{progress}</span>}
              <button
                className="primary-button"
                disabled={busy || rows.some((r) => !r.name.trim())}
                onClick={() => void uploadAll()}
              >
                <UploadCloud size={18} />
                {busy ? `업로드 중... ${progress}` : `전체 업로드 (${rows.length}개) · 누적 반영`}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="panel">
        <div className="section-head">
          <span className="step">2</span> 산업 목록
          <span className="section-hint">
            {industries.length}개 · 최신 {freshCount} / 지연 {industries.length - freshCount}
          </span>
        </div>
        {industries.length > 0 && (
          <div className="list-controls">
            <div className="search-field">
              <Search size={15} />
              <input
                className="industry-search"
                placeholder="산업·카테고리 검색"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="list-filter-row">
              <div className="cat-select">
                <Filter size={14} />
                <select value={catFilter} onChange={(event) => setCatFilter(event.target.value)}>
                  <option value="">전체 카테고리</option>
                  {allCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="collapse-all"
                onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(groups.map(([cat]) => cat)))}
              >
                {allCollapsed ? <ChevronsUpDown size={14} /> : <ChevronsDownUp size={14} />}
                {allCollapsed ? "모두 펼치기" : "모두 접기"}
              </button>
            </div>
          </div>
        )}
        {industries.length ? (
          groups.map(([cat, list], gi) => (
            <div className="industry-group" key={cat}>
              <div
                className={`industry-group-bar${dragOverCat === cat && dragCat && dragCat !== cat ? " drag-over" : ""}`}
                draggable
                onDragStart={() => setDragCat(cat)}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOverCat(cat);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dragCat) void reorderCategories(dragCat, cat);
                  setDragCat(null);
                  setDragOverCat(null);
                }}
                onDragEnd={() => {
                  setDragCat(null);
                  setDragOverCat(null);
                }}
              >
                <span className="cat-grip" title="드래그하여 순서 변경">
                  <GripVertical size={14} />
                </span>
                <button
                  type="button"
                  className={isCollapsed(cat) ? "industry-group-head collapsed" : "industry-group-head"}
                  style={{ ["--cat-hue" as string]: String(categoryHue(cat, gi)) }}
                  onClick={() => toggleGroup(cat)}
                >
                  {isCollapsed(cat) ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  {cat} <span>{list.length}</span>
                </button>
                <button
                  type="button"
                  className="cat-delete"
                  title="카테고리 삭제 (안의 산업 모두 삭제)"
                  onClick={() => setConfirmDeleteCat(cat)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {!isCollapsed(cat) && (
              <div className="industry-grid">
                {list.map((ind) => (
                  <div className="industry-card" key={ind.industry_id}>
                    <div className="industry-card-head">
                      <strong>{ind.name}</strong>
                      <div className="industry-card-actions">
                        <button className="report-delete" title="이름·카테고리 편집" onClick={() => openEdit(ind)}>
                          <Pencil size={14} />
                        </button>
                        <button
                          className="report-delete"
                          title="산업 삭제"
                          onClick={() => setConfirmDeleteId(ind.industry_id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                    <div className="industry-meta">
                      차트 {ind.charts}개 · 누적 {ind.weeks}주
                    </div>
                    <div className="industry-meta">
                      <span className={isStale(ind.last_week) ? "badge stale" : "badge fresh"}>
                        {isStale(ind.last_week) ? "지연" : "최신"}
                      </span>{" "}
                      마지막 {ind.last_week ?? "—"}
                    </div>
                    <button
                      className="secondary-button"
                      style={{ marginTop: 8 }}
                      onClick={() => onShowDashboards?.()}
                    >
                      <Eye size={16} /> 대시보드 보기
                    </button>
                  </div>
                ))}
              </div>
              )}
            </div>
          ))
        ) : (
          <span className="empty-hint">아직 등록된 산업이 없습니다. 위에서 엑셀을 업로드해 시작하세요.</span>
        )}
      </section>

      {message && <div className={message.includes("실패") || message.includes("못") ? "error" : "notice"}>{message}</div>}

      {confirmDeleteId && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteId(null)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3>산업 데이터셋을 삭제할까요?</h3>
            <p>
              <strong>{industries.find((i) => i.industry_id === confirmDeleteId)?.name ?? "이 산업"}</strong>
              {" 의 누적 데이터와 대시보드가 모두 제거되며 되돌릴 수 없습니다."}
            </p>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setConfirmDeleteId(null)}>
                취소
              </button>
              <button className="danger-button" onClick={() => void remove(confirmDeleteId)}>
                <Trash2 size={16} /> 삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteCat && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteCat(null)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3>카테고리를 삭제할까요?</h3>
            <p>
              <strong>{confirmDeleteCat}</strong>
              {" 카테고리와 그 안의 "}
              <strong>
                {industries.filter((i) => (i.category?.trim() || "미분류") === confirmDeleteCat).length}개 산업
              </strong>
              {" 의 누적 데이터·대시보드가 모두 제거됩니다. 되돌릴 수 없습니다."}
            </p>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setConfirmDeleteCat(null)}>
                취소
              </button>
              <button className="danger-button" onClick={() => void deleteCategory(confirmDeleteCat)}>
                <Trash2 size={16} /> 카테고리 삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {editId && (
        <div className="modal-overlay" onClick={() => setEditId(null)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3>산업 정보 편집</h3>
            <p style={{ marginBottom: 12 }}>이름과 카테고리(섹터)만 수정합니다 — 누적 데이터는 그대로 유지됩니다.</p>
            <div className="field key" style={{ marginBottom: 12 }}>
              <label>산업 이름</label>
              <input value={editName} onChange={(event) => setEditName(event.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 12 }}>
              <label>카테고리 (섹터)</label>
              <input
                placeholder="비우면 미분류"
                value={editCategory}
                onChange={(event) => setEditCategory(event.target.value)}
                list="industry-categories"
              />
            </div>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setEditId(null)}>
                취소
              </button>
              <button className="primary-button" onClick={() => void saveEdit()}>
                <Check size={16} /> 저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Member + admin industry dashboards: browse industries by category, open one to
// view its charts. Reads the (small) industries index, then the chosen report.
function DashboardsPage({ api, user }: { api: Api | null; user: AuthUser }) {
  const [industries, setIndustries] = useState<IndustrySummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  // Start with every category collapsed; the user expands one and clicks an
  // industry to see its charts. `null` = not yet initialised from the data.
  const [collapsed, setCollapsed] = useState<Set<string> | null>(null);
  const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
  const [clusters, setClusters] = useState<ClusterSummary[]>([]);
  const [message, setMessage] = useState("");
  const canUseApi = Boolean(config.apiBaseUrl) || config.localMock;
  const isAdmin = user.roles.includes("Admin");

  function toggleGroup(cat: string) {
    setCollapsed((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  useEffect(() => {
    if (!api || !canUseApi) return;
    api
      .listIndustries()
      .then((items) => {
        setIndustries(items);
        // Default: all categories collapsed, nothing selected (charts hidden).
        const cats = new Set(items.map((i) => i.category?.trim() || "미분류"));
        setCollapsed((prev) => prev ?? cats);
      })
      .catch((caught) => setMessage(caught instanceof Error ? caught.message : "산업 목록을 불러오지 못했습니다."));
    api.getCategoryOrder().then(setCategoryOrder).catch(() => undefined);
    if (isAdmin) api.listClusters().then(setClusters).catch(() => undefined);
  }, [api, canUseApi, isAdmin]);

  const selected = industries.find((i) => i.industry_id === selectedId);

  // Admin: pin a chart of the selected industry into a cluster.
  async function addChartToCluster(clusterId: string, chartId: string) {
    if (!api || !selected) return;
    try {
      await api.addClusterItem(clusterId, { industry_id: selected.industry_id, chart_id: chartId });
      setClusters(await api.listClusters());
      setMessage(`클러스터에 추가했습니다.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "클러스터 추가에 실패했습니다.");
    }
  }

  async function createClusterWithChart(chartId: string) {
    if (!api || !selected) return;
    const name = window.prompt("새 클러스터 이름을 입력하세요");
    if (!name?.trim()) return;
    try {
      const created = await api.createCluster(name.trim());
      await api.addClusterItem(created.cluster_id, { industry_id: selected.industry_id, chart_id: chartId });
      setClusters(await api.listClusters());
      setMessage(`'${created.name}' 클러스터를 만들고 차트를 추가했습니다.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "클러스터 생성에 실패했습니다.");
    }
  }

  const picker: ClusterPicker | undefined =
    isAdmin && selected ? { clusters, onAdd: addChartToCluster, onCreate: createClusterWithChart } : undefined;

  useEffect(() => {
    if (!api || !canUseApi || !selected?.report_id) {
      setDetail(null);
      return;
    }
    api
      .getReport(selected.report_id)
      .then((report) => {
        setMessage("");
        setDetail(report);
      })
      .catch((caught) => setMessage(caught instanceof Error ? caught.message : "대시보드를 불러오지 못했습니다."));
  }, [api, canUseApi, selected?.report_id]);

  const q = search.trim().toLowerCase();
  const allCategories = [...new Set(industries.map((ind) => ind.category?.trim() || "미분류"))].sort((a, b) =>
    a.localeCompare(b)
  );
  const filtered = industries.filter((ind) => {
    const cat = ind.category?.trim() || "미분류";
    if (catFilter && cat !== catFilter) return false;
    return !q || ind.name.toLowerCase().includes(q) || cat.toLowerCase().includes(q);
  });
  const groups = groupByCategory(filtered, categoryOrder);
  const isCollapsed = (cat: string) => collapsed?.has(cat) ?? true;
  const allCollapsed = groups.length > 0 && groups.every(([cat]) => isCollapsed(cat));

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <h1>산업 대시보드</h1>
          <p>카테고리를 펼치고 산업을 클릭하면 차트가 나타납니다 — 매주 갱신됩니다.</p>
        </div>
      </header>
      {message && <div className="error">{message}</div>}
      <section className="report-layout">
        <div className="panel report-list dash-list">
          <h2>산업</h2>
          {industries.length > 0 && (
            <div className="list-controls">
              <div className="search-field">
                <Search size={15} />
                <input
                  className="industry-search"
                  placeholder="산업·섹터 검색"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="list-filter-row">
                <div className="cat-select">
                  <Filter size={14} />
                  <select value={catFilter} onChange={(event) => setCatFilter(event.target.value)}>
                    <option value="">전체 카테고리</option>
                    {allCategories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="collapse-all"
                  onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(groups.map(([cat]) => cat)))}
                >
                  {allCollapsed ? <ChevronsUpDown size={14} /> : <ChevronsDownUp size={14} />}
                  {allCollapsed ? "모두 펼치기" : "모두 접기"}
                </button>
              </div>
            </div>
          )}
          {groups.map(([cat, list], gi) => (
            <div className="industry-group" key={cat}>
              <button
                type="button"
                className={isCollapsed(cat) ? "industry-group-head collapsed" : "industry-group-head"}
                style={{ ["--cat-hue" as string]: String(categoryHue(cat, gi)) }}
                onClick={() => toggleGroup(cat)}
              >
                {isCollapsed(cat) ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                {cat} <span>{list.length}</span>
              </button>
              {!isCollapsed(cat) &&
                list.map((ind) => (
                  <button
                    key={ind.industry_id}
                    className={selectedId === ind.industry_id ? "report-link active" : "report-link"}
                    onClick={() => setSelectedId(ind.industry_id)}
                  >
                    <strong>{ind.name}</strong>
                    <span>차트 {ind.charts}개 · {ind.last_week ?? "—"}</span>
                  </button>
                ))}
            </div>
          ))}
          {!industries.length && <EmptyState text="아직 산업 대시보드가 없습니다." />}
        </div>
        <div className="chart-grid">
          {detail ? (
            detail.charts.map((chart) => (
              <ChartCard chart={chart} key={chart.id} showRaw={isAdmin} cluster={picker} />
            ))
          ) : (
            <EmptyState text="산업을 선택해 대시보드를 확인하세요." />
          )}
        </div>
      </section>
    </div>
  );
}

// 지표 클러스터: view admin-curated cross-industry chart collections. A cluster
// stores only (industry, chart) references; the server resolves them into the
// live charts in one request, so the page makes a single round-trip and only
// downloads the charts it needs (not N full industry reports).
function ClustersPage({ api, user }: { api: Api | null; user: AuthUser }) {
  const [clusters, setClusters] = useState<ClusterSummary[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState<ResolvedCluster | null>(null);
  const [resolved, setResolved] = useState<ResolvedClusterChart[]>([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const canUseApi = Boolean(config.apiBaseUrl) || config.localMock;
  const isAdmin = user.roles.includes("Admin");

  // Drag-reorder clusters (admin only); persist the new order.
  async function moveCluster(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || to >= clusters.length) return;
    const next = [...clusters];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setClusters(next); // optimistic
    try {
      await api?.reorderClusters(next.map((c) => c.cluster_id));
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "순서 저장에 실패했습니다.");
    }
  }

  async function refreshClusters() {
    if (!api) return;
    const list = await api.listClusters();
    setClusters(list);
    setSelectedId((prev) => prev || list[0]?.cluster_id || "");
  }

  useEffect(() => {
    if (!api || !canUseApi) return;
    api
      .listClusters()
      .then((list) => {
        setClusters(list);
        setSelectedId((prev) => prev || list[0]?.cluster_id || "");
      })
      .catch((caught) => setMessage(caught instanceof Error ? caught.message : "클러스터를 불러오지 못했습니다."));
  }, [api, canUseApi]);

  // One request resolves the selected cluster server-side into its live charts.
  // Re-runs on selection change or a local mutation (tick).
  useEffect(() => {
    if (!api || !canUseApi || !selectedId) {
      setDetail(null);
      setResolved([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .getClusterResolved(selectedId)
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
        setResolved(Array.isArray(data?.charts) ? data.charts : []);
      })
      .catch((caught) => {
        if (!cancelled) setMessage(caught instanceof Error ? caught.message : "클러스터를 불러오지 못했습니다.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, canUseApi, selectedId, tick]);

  async function createCluster() {
    if (!api) return;
    const name = window.prompt("새 클러스터 이름을 입력하세요");
    if (!name?.trim()) return;
    try {
      const created = await api.createCluster(name.trim());
      await refreshClusters();
      setSelectedId(created.cluster_id);
      setMessage(`'${created.name}' 클러스터를 만들었습니다. 산업 대시보드에서 차트를 추가하세요.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "생성에 실패했습니다.");
    }
  }

  async function renameCluster() {
    if (!api || !detail) return;
    const name = window.prompt("클러스터 이름 변경", detail.name);
    if (!name?.trim() || name.trim() === detail.name) return;
    try {
      await api.updateCluster(detail.cluster_id, { name: name.trim() });
      await refreshClusters();
      setTick((t) => t + 1);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "이름 변경에 실패했습니다.");
    }
  }

  async function deleteCluster() {
    if (!api || !detail) return;
    if (!window.confirm(`'${detail.name}' 클러스터를 삭제할까요? (차트 원본은 그대로 유지됩니다)`)) return;
    try {
      await api.deleteCluster(detail.cluster_id);
      setSelectedId("");
      await refreshClusters();
      setMessage("클러스터를 삭제했습니다.");
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "삭제에 실패했습니다.");
    }
  }

  async function removeItem(target: ClusterItem) {
    if (!api || !detail) return;
    const items = detail.items.filter(
      (it) => !(it.industry_id === target.industry_id && it.chart_id === target.chart_id)
    );
    try {
      await api.updateCluster(detail.cluster_id, { items });
      await refreshClusters();
      setTick((t) => t + 1);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "제거에 실패했습니다.");
    }
  }

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <h1>지표 클러스터</h1>
          <p>여러 산업에서 고른 지표를 한 화면에 모아 봅니다 — 매주 데이터가 갱신되면 자동 반영됩니다.</p>
        </div>
        {isAdmin && (
          <button type="button" className="primary-button" onClick={createCluster}>
            <Star size={16} /> 새 클러스터
          </button>
        )}
      </header>
      {message && <div className="notice">{message}</div>}
      <section className="report-layout">
        <div className="panel report-list dash-list">
          <h2>클러스터</h2>
          {clusters.length ? (
            clusters.map((c, index) => (
              <div
                key={c.cluster_id}
                className={`cluster-row${dragOverIdx === index && dragIdx !== null && dragIdx !== index ? " drag-over" : ""}`}
                draggable={isAdmin}
                onDragStart={isAdmin ? () => setDragIdx(index) : undefined}
                onDragOver={
                  isAdmin
                    ? (event) => {
                        event.preventDefault();
                        setDragOverIdx(index);
                      }
                    : undefined
                }
                onDrop={
                  isAdmin
                    ? (event) => {
                        event.preventDefault();
                        if (dragIdx !== null) void moveCluster(dragIdx, index);
                        setDragIdx(null);
                        setDragOverIdx(null);
                      }
                    : undefined
                }
                onDragEnd={
                  isAdmin
                    ? () => {
                        setDragIdx(null);
                        setDragOverIdx(null);
                      }
                    : undefined
                }
              >
                {isAdmin && (
                  <span className="cat-grip" title="드래그하여 순서 변경">
                    <GripVertical size={14} />
                  </span>
                )}
                <button
                  type="button"
                  className={selectedId === c.cluster_id ? "report-link active" : "report-link"}
                  onClick={() => setSelectedId(c.cluster_id)}
                >
                  <strong>{c.name}</strong>
                  <span>지표 {c.count}개</span>
                </button>
              </div>
            ))
          ) : (
            <EmptyState
              text={
                isAdmin
                  ? "아직 클러스터가 없습니다. '새 클러스터'로 만든 뒤 산업 대시보드의 차트에서 추가하세요."
                  : "아직 공유된 클러스터가 없습니다."
              }
            />
          )}
        </div>
        <div className="chart-grid">
          {detail && isAdmin && (
            <div className="cluster-toolbar">
              <strong>{detail.name}</strong>
              <div>
                <button type="button" className="secondary-button" onClick={renameCluster}>
                  <Pencil size={14} /> 이름 변경
                </button>
                <button type="button" className="report-delete" onClick={deleteCluster} title="클러스터 삭제">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )}
          {!detail ? (
            <EmptyState text="클러스터를 선택하세요." />
          ) : loading && !resolved.length ? (
            <EmptyState text="불러오는 중…" />
          ) : resolved.length ? (
            resolved.map(({ industry_id, industry_name, chart }) => (
              <div className="cluster-chart" key={`${industry_id}:${chart.id}`}>
                <div className="cluster-chart-head">
                  <span className="cluster-chart-industry">{industry_name}</span>
                  {isAdmin && (
                    <button
                      type="button"
                      className="report-delete"
                      title="클러스터에서 제거"
                      onClick={() => removeItem({ industry_id, chart_id: chart.id })}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <ChartCard chart={chart} showRaw={isAdmin} />
              </div>
            ))
          ) : (
            <EmptyState text="이 클러스터에 담긴 지표가 없습니다. 산업 대시보드의 차트에서 '클러스터'로 추가하세요." />
          )}
        </div>
      </section>
    </div>
  );
}

const chartColors = ["#1d4ed8", "#0f766e", "#b45309"];

// Report list groups, keyed by the admin-assigned category (publish/edit time).
// Insertion order follows the report order (newest-first), so the most recently
// updated category floats to the top; uncategorised reports fall under 미분류.
function groupReportsByCategory(items: ReportSummary[]): Array<[string, ReportSummary[]]> {
  const groups = new Map<string, ReportSummary[]>();
  for (const report of items) {
    const key = report.category?.trim() || "미분류";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(report);
  }
  return [...groups.entries()];
}

// Tool icon shown in place of the source text — scatter-style tools get the
// scatter glyph, time-series tools the line glyph; hover reveals the tool name.
function sourceIcon(source?: string): ReactNode {
  if (source && (source.includes("산점도") || source.includes("모멘텀") || source.includes("분위"))) {
    return <ScatterChart size={14} />;
  }
  return <LineChart size={14} />;
}

// A stable hue per category so its accent colour stays the same across renders
// and collapsed categories read as distinct colour chips. Hashes the name (so
// the colour follows the category, not its position) and nudges by index to
// keep neighbours apart.
function categoryHue(cat: string, index = 0): number {
  let h = 0;
  for (let i = 0; i < cat.length; i += 1) h = (h * 31 + cat.charCodeAt(i)) % 360;
  return (h + index * 47) % 360;
}

// Picks the right chart variant for a spec. Shared by the in-grid card and the
// zoom modal so the enlarged view is pixel-identical, just rendered wider.
function chartInner(chart: ChartSpec): ReactNode {
  if (chart.kind === "scatter") return <ScatterChartCard chart={chart} />;
  if (chart.dualAxis) return <DualAxisChartCard chart={chart} />;
  if (chart.kind === "line") return <LineChartCard chart={chart} />;
  return <BarChartCard chart={chart} />;
}

// Capability passed to a chart card so an admin can drop the chart into a
// cluster. `onAdd` appends to an existing cluster; `onCreate` makes a new one
// (the parent prompts for the name) and adds the chart to it.
interface ClusterPicker {
  clusters: ClusterSummary[];
  onAdd: (clusterId: string, chartId: string) => void;
  onCreate: (chartId: string) => void;
}

// Every chart gets a "확대" (zoom) button; `showRaw` (admin-only) adds
// "원시 데이터"; `cluster` (admin-only) adds a "클러스터" menu to pin the chart
// into a named cross-industry cluster.
function ChartCard({
  chart,
  showRaw,
  cluster
}: {
  chart: ChartSpec;
  showRaw?: boolean;
  cluster?: ClusterPicker;
}) {
  const [showTable, setShowTable] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [pick, setPick] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);

  // Close the cluster menu on an outside click or Escape — not on mouse-leave,
  // which fired before the user could reach a menu item and pick it.
  useEffect(() => {
    if (!pick) return;
    function onDown(event: MouseEvent) {
      if (addRef.current && !addRef.current.contains(event.target as Node)) setPick(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setPick(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pick]);

  return (
    <div className="chart-card-raw">
      <div className="chart-actions">
        {cluster && (
          <div className="cluster-add" ref={addRef}>
            <button type="button" className="chart-action" onClick={() => setPick((v) => !v)}>
              <Star size={14} /> 클러스터
            </button>
            {pick && (
              <div className="cluster-menu">
                {cluster.clusters.length === 0 && <span className="cluster-menu-empty">아직 클러스터가 없습니다</span>}
                {cluster.clusters.map((c) => (
                  <button
                    key={c.cluster_id}
                    type="button"
                    onClick={() => {
                      cluster.onAdd(c.cluster_id, chart.id);
                      setPick(false);
                    }}
                  >
                    {c.name}
                  </button>
                ))}
                <button
                  type="button"
                  className="cluster-menu-new"
                  onClick={() => {
                    cluster.onCreate(chart.id);
                    setPick(false);
                  }}
                >
                  ＋ 새 클러스터…
                </button>
              </div>
            )}
          </div>
        )}
        <button type="button" className="chart-action" onClick={() => setZoom(true)}>
          <Maximize2 size={14} /> 확대
        </button>
        {showRaw && (
          <button type="button" className="chart-action" onClick={() => setShowTable(true)}>
            <Table2 size={14} /> 원시 데이터
          </button>
        )}
      </div>
      {chartInner(chart)}
      {zoom && <ChartZoomModal chart={chart} onClose={() => setZoom(false)} />}
      {showTable && <RawDataModal chart={chart} onClose={() => setShowTable(false)} />}
    </div>
  );
}

// Enlarged single-chart view. The chart SVGs are responsive (viewBox + width
// 100%), so rendering the same variant in a wide modal scales it up cleanly.
function ChartZoomModal({ chart, onClose }: { chart: ChartSpec; onClose: () => void }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-chart" onClick={(event) => event.stopPropagation()}>
        <div className="zoom-modal-head">
          <h3>{chart.title}</h3>
          <button type="button" className="icon-close" onClick={onClose} aria-label="닫기">
            <X size={18} />
          </button>
        </div>
        <div className="zoom-chart">{chartInner(chart)}</div>
      </div>
    </div>
  );
}

function BarChartCard({ chart }: { chart: ChartSpec }) {
  const max = Math.max(...chart.series.flatMap((series) => series.values).filter((v) => Number.isFinite(v)), 1);
  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <h3>{chart.title}</h3>
        <span>{chart.unit}</span>
      </div>
      <div className="chart-area">
        {chart.labels.map((label, index) => (
          <div className="bar-group" key={label}>
            <div className="bars">
              {chart.series.map((series, seriesIndex) => (
                <span
                  key={series.name}
                  title={`${series.name}: ${series.values[index]}`}
                  style={{
                    height: `${Math.max(6, (series.values[index] / max) * 100)}%`,
                    background: seriesIndex % 3 === 0 ? "#1d4ed8" : seriesIndex % 3 === 1 ? "#0f766e" : "#b45309"
                  }}
                />
              ))}
            </div>
            <small>{label}</small>
          </div>
        ))}
      </div>
      <div className="legend">
        {chart.series.map((series, index) => (
          <span key={series.name}>
            <i style={{ background: index % 3 === 0 ? "#1d4ed8" : index % 3 === 1 ? "#0f766e" : "#b45309" }} />
            {series.name}
          </span>
        ))}
      </div>
    </article>
  );
}

function fmtRaw(v: number): string {
  if (!Number.isFinite(v)) return "—";
  return String(Math.round(v * 10000) / 10000);
}

// Admin raw-data viewer: the dates × series values behind one chart, newest
// first, with a CSV export. Pure client-side over the already-loaded chart.
function RawDataModal({ chart, onClose }: { chart: ChartSpec; onClose: () => void }) {
  const rows = chart.labels.map((label, i) => ({ label, i })).reverse();

  function downloadCsv() {
    const header = ["date", ...chart.series.map((s) => s.name)];
    const body = rows.map((r) => [r.label, ...chart.series.map((s) => fmtRaw(s.values[r.i]))]);
    const csv = [header, ...body].map((line) => line.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${(chart.title || "chart").replace(/[\\/:*?"<>|]+/g, "_")}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(event) => event.stopPropagation()}>
        <div className="raw-modal-head">
          <h3>{chart.title} · 원시 데이터</h3>
          <span>
            {rows.length}주 · {chart.series.length}개 시리즈 (관리자 전용)
          </span>
        </div>
        <div className="raw-table-scroll">
          <table className="raw-table">
            <thead>
              <tr>
                <th>날짜</th>
                {chart.series.map((s, j) => (
                  <th key={j}>{s.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  {chart.series.map((s, j) => (
                    <td key={j}>{fmtRaw(s.values[r.i])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="modal-actions">
          <button className="secondary-button" onClick={downloadCsv}>
            <Download size={16} /> CSV
          </button>
          <button className="primary-button" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

function LineChartCard({ chart }: { chart: ChartSpec }) {
  const all = chart.series.flatMap((series) => series.values).filter((v) => Number.isFinite(v));
  const dataMax = all.length ? Math.max(...all) : 1;
  const dataMin = all.length ? Math.min(...all) : 0;
  // Use manual Y bounds when provided, else auto-scale to the data.
  const lo = chart.yMin ?? dataMin;
  const hi = chart.yMax ?? dataMax;
  const min = Math.min(lo, hi);
  const max = Math.max(lo, hi);
  const width = 520;
  const height = 240;
  const padX = 44;
  const padY = 24;
  const count = chart.labels.length;
  const x = (index: number) => (count <= 1 ? padX : padX + (index * (width - padX * 2)) / (count - 1));
  const y = (value: number) => {
    const t = (value - min) / (max - min || 1);
    const clamped = Math.max(0, Math.min(1, t)); // keep out-of-range points at the edge
    return height - padY - clamped * (height - padY * 2);
  };
  // Keep dense series readable: markers only for small sets, ~7 x-axis ticks.
  const showMarkers = count <= 40;
  const tickEvery = Math.max(1, Math.ceil(count / 7));
  const fmt = (value: number) => {
    const rounded = Math.round(value * 100) / 100;
    return Math.abs(rounded) >= 1000 ? rounded.toLocaleString() : String(rounded);
  };
  const steps = 4;

  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <h3>{chart.title}</h3>
        <span>{chart.unit}</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto" }} role="img" aria-label={chart.title}>
        {Array.from({ length: steps + 1 }).map((_, i) => {
          const value = min + ((max - min) * i) / steps;
          const gy = y(value);
          return (
            <g key={`grid-${i}`}>
              <line x1={padX} y1={gy} x2={width - padX} y2={gy} stroke="#eef2f7" />
              <text x={padX - 6} y={gy + 3} fontSize={9} textAnchor="end" fill="#94a3b8">{fmt(value)}</text>
            </g>
          );
        })}
        <line x1={padX} y1={padY} x2={padX} y2={height - padY} stroke="#cbd5e1" />
        <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} stroke="#cbd5e1" />
        {chart.series.map((series, seriesIndex) => (
          <polyline
            key={series.name}
            fill="none"
            stroke={chartColors[seriesIndex % chartColors.length]}
            strokeWidth={2}
            points={series.values
              .map((value, index) => (Number.isFinite(value) ? `${x(index)},${y(value)}` : null))
              .filter((p): p is string => p !== null)
              .join(" ")}
          />
        ))}
        {showMarkers &&
          chart.series.map((series, seriesIndex) =>
            series.values.map((value, index) =>
              !Number.isFinite(value) ? null : (
                <circle
                  key={`${series.name}-${index}`}
                  cx={x(index)}
                  cy={y(value)}
                  r={2.5}
                  fill={chartColors[seriesIndex % chartColors.length]}
                />
              )
            )
          )}
        {chart.labels.map((label, index) =>
          index % tickEvery === 0 ? (
            <text key={label + index} x={x(index)} y={height - 6} fontSize={9} textAnchor="middle" fill="#64748b">
              {label.length > 10 ? `${label.slice(0, 10)}…` : label}
            </text>
          ) : null
        )}
      </svg>
      <div className="legend">
        {chart.series.map((series, index) => (
          <span key={series.name}>
            <i style={{ background: chartColors[index % chartColors.length] }} />
            {series.name}
          </span>
        ))}
      </div>
    </article>
  );
}

function DualAxisChartCard({ chart }: { chart: ChartSpec }) {
  const leftSeries = chart.series.filter((s) => s.axis !== "right");
  const rightSeries = chart.series.filter((s) => s.axis === "right");
  const leftVals = leftSeries.flatMap((s) => s.values).filter((v) => Number.isFinite(v));
  const rightVals = rightSeries.flatMap((s) => s.values).filter((v) => Number.isFinite(v));
  const lMin = chart.yMin ?? (leftVals.length ? Math.min(...leftVals) : 0);
  const lMax = chart.yMax ?? (leftVals.length ? Math.max(...leftVals) : 1);
  const rMin = chart.rightMin ?? (rightVals.length ? Math.min(...rightVals) : 0);
  const rMax = chart.rightMax ?? (rightVals.length ? Math.max(...rightVals) : 1);
  const width = 560;
  const height = 260;
  const padX = 54;
  const padY = 26;
  const count = chart.labels.length;
  const x = (index: number) => (count <= 1 ? padX : padX + (index * (width - padX * 2)) / (count - 1));
  const scale = (value: number, min: number, max: number) => {
    const t = (value - min) / (max - min || 1);
    const clamped = Math.max(0, Math.min(1, t));
    return height - padY - clamped * (height - padY * 2);
  };
  const yLeft = (v: number) => scale(v, lMin, lMax);
  const yRight = (v: number) => scale(v, rMin, rMax);
  const fmt = (v: number) => (Math.abs(v) >= 1000 ? v.toLocaleString() : String(Math.round(v * 100) / 100));
  const showMarkers = count <= 40;
  const tickEvery = Math.max(1, Math.ceil(count / 7));
  const colorOf = (s: ChartSpec["series"][number], i: number) => s.color ?? chartColors[i % chartColors.length];

  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <h3>{chart.title}</h3>
        <span>
          좌: {chart.unit ?? "값"} · 우: {chart.rightUnit ?? "값"}
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto" }} role="img" aria-label={chart.title}>
        {Array.from({ length: 5 }).map((_, i) => {
          const f = i / 4;
          const gy = height - padY - f * (height - padY * 2);
          const leftValue = lMin + (lMax - lMin) * f;
          const rightValue = rMin + (rMax - rMin) * f;
          return (
            <g key={`grid-${i}`}>
              <line x1={padX} y1={gy} x2={width - padX} y2={gy} stroke="#eef2f7" />
              <text x={padX - 6} y={gy + 3} fontSize={9} textAnchor="end" fill="#94a3b8">{fmt(leftValue)}</text>
              <text x={width - padX + 6} y={gy + 3} fontSize={9} textAnchor="start" fill="#94a3b8">{fmt(rightValue)}</text>
            </g>
          );
        })}
        <line x1={padX} y1={padY} x2={padX} y2={height - padY} stroke="#cbd5e1" />
        <line x1={width - padX} y1={padY} x2={width - padX} y2={height - padY} stroke="#cbd5e1" />
        <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} stroke="#cbd5e1" />
        {chart.series.map((s, si) => {
          const yf = s.axis === "right" ? yRight : yLeft;
          return (
            <polyline
              key={`${s.name}-${si}`}
              fill="none"
              stroke={colorOf(s, si)}
              strokeWidth={2}
              strokeDasharray={s.dashed ? "5 4" : undefined}
              points={s.values
                .map((v, i) => (Number.isFinite(v) ? `${x(i)},${yf(v)}` : null))
                .filter((p): p is string => p !== null)
                .join(" ")}
            />
          );
        })}
        {showMarkers &&
          chart.series.map((s, si) => {
            const yf = s.axis === "right" ? yRight : yLeft;
            return s.values.map((v, i) =>
              Number.isFinite(v) ? (
                <circle key={`${s.name}-${si}-${i}`} cx={x(i)} cy={yf(v)} r={2} fill={colorOf(s, si)} />
              ) : null
            );
          })}
        {chart.labels.map((label, i) =>
          i % tickEvery === 0 ? (
            <text key={label + i} x={x(i)} y={height - 6} fontSize={9} textAnchor="middle" fill="#64748b">
              {label.length > 10 ? `${label.slice(0, 10)}…` : label}
            </text>
          ) : null
        )}
      </svg>
      <div className="legend">
        {chart.series.map((s, i) => (
          <span key={`${s.name}-${i}`}>
            <i style={{ background: colorOf(s, i), opacity: s.dashed ? 0.5 : 1 }} />
            {s.name} {s.axis === "right" ? "(우)" : "(좌)"}
          </span>
        ))}
      </div>
    </article>
  );
}

function ScatterChartCard({ chart }: { chart: ChartSpec }) {
  const points = chart.points ?? [];
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const rawXMin = chart.xMin ?? (xs.length ? Math.min(...xs) : 0);
  const rawXMax = chart.xMax ?? (xs.length ? Math.max(...xs) : 1);
  const xPad = (rawXMax - rawXMin) * 0.08 || 1;
  const x0 = rawXMin - xPad;
  const x1 = rawXMax + xPad;
  const yMin = chart.yMin ?? (ys.length ? Math.min(...ys) : 0);
  const yMax = chart.yMax ?? (ys.length ? Math.max(...ys) : 100);
  const width = 620;
  const height = 380;
  const padL = 56;
  const padR = 28;
  const padT = 18;
  const padB = 46;
  const X = (v: number) => padL + ((v - x0) / (x1 - x0 || 1)) * (width - padL - padR);
  const Y = (v: number) => height - padB - ((v - yMin) / (yMax - yMin || 1)) * (height - padT - padB);
  const fmt = (v: number) => {
    const r = Math.round(v * 100) / 100;
    return Math.abs(r) >= 1000 ? r.toLocaleString() : String(r);
  };
  const steps = 5;

  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <h3>{chart.title}</h3>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto" }} role="img" aria-label={chart.title}>
        {Array.from({ length: steps + 1 }).map((_, i) => {
          const f = i / steps;
          const gy = height - padB - f * (height - padT - padB);
          const value = yMin + (yMax - yMin) * f;
          return (
            <g key={`yg-${i}`}>
              <line x1={padL} y1={gy} x2={width - padR} y2={gy} stroke="#eef2f7" />
              <text x={padL - 6} y={gy + 3} fontSize={9} textAnchor="end" fill="#94a3b8">{fmt(value)}%</text>
            </g>
          );
        })}
        {Array.from({ length: steps + 1 }).map((_, i) => {
          const f = i / steps;
          const gx = padL + f * (width - padL - padR);
          const value = x0 + (x1 - x0) * f;
          return (
            <g key={`xg-${i}`}>
              <line x1={gx} y1={padT} x2={gx} y2={height - padB} stroke="#f3f6fa" />
              <text x={gx} y={height - padB + 14} fontSize={9} textAnchor="middle" fill="#64748b">
                {fmt(value)}
                {chart.xUnit ?? ""}
              </text>
            </g>
          );
        })}
        <line x1={padL} y1={padT} x2={padL} y2={height - padB} stroke="#cbd5e1" />
        <line x1={padL} y1={height - padB} x2={width - padR} y2={height - padB} stroke="#cbd5e1" />
        {/* Quadrant zero-lines, when 0 falls inside the plotted range. */}
        {x0 < 0 && x1 > 0 && (
          <line x1={X(0)} y1={padT} x2={X(0)} y2={height - padB} stroke="#94a3b8" strokeDasharray="4 3" />
        )}
        {yMin < 0 && yMax > 0 && (
          <line x1={padL} y1={Y(0)} x2={width - padR} y2={Y(0)} stroke="#94a3b8" strokeDasharray="4 3" />
        )}
        {points.map((p, i) => (
          <g key={`${p.label}-${i}`}>
            <circle cx={X(p.x)} cy={Y(p.y)} r={5} fill={p.color ?? chartColors[i % chartColors.length]} />
            <text x={X(p.x) + 8} y={Y(p.y) - 8} fontSize={11} fontWeight={600} fill={p.color ?? chartColors[i % chartColors.length]}>
              {p.label}
            </text>
          </g>
        ))}
        <text x={(padL + width - padR) / 2} y={height - 6} fontSize={11} textAnchor="middle" fill="#475569">
          {chart.xAxisLabel ?? "예측값 (X)"}
        </text>
        <text x={14} y={(padT + height - padB) / 2} fontSize={11} textAnchor="middle" fill="#475569" transform={`rotate(-90 14 ${(padT + height - padB) / 2})`}>
          {chart.yAxisLabel ?? "분위(%)"}
        </text>
      </svg>
      {/* Category legend (scatter series carry name + colour, no values). */}
      {chart.series.length > 0 && (
        <div className="legend">
          {chart.series.map((s) => (
            <span key={s.name}>
              <i style={{ background: s.color ?? "#64748b" }} />
              {s.name}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

function EmptyState({ text }: { text: string }) {
  return <div className="empty-state">{text}</div>;
}
